import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Title, Text, Button, TextInput, Textarea } from '@tremor/react'
import { 
    XMarkIcon, 
    PlusIcon, 
    TrashIcon, 
    PhotoIcon,
    ClockIcon,
    EyeIcon,
    QuestionMarkCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    PlayIcon,
    CheckIcon,
    Bars3Icon,
    SpeakerWaveIcon,
    MicrophoneIcon,
    StopIcon
} from '@heroicons/react/24/outline'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
    useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { sanitizeUserInput } from '../../utils/security'
import { uploadQuestionMedia, createQuestionnaire, updateQuestionnaire, createQuestion, updateQuestion as updateQuestionAPI, getQuestionnaire, deleteQuestion as deleteQuestionAPI, uploadOptionMedia, deleteOptionMedia } from '../../Requests/QuestionnaireRequests'
import QuestionnaireChunkedUploader from './QuestionnaireChunkedUploader'

// Utility function to generate hash for question data
const generateQuestionHash = (question) => {
    // Create a normalized object with only the fields that matter for change detection
    const normalizedQuestion = {
        question_text: question.question_text || '',
        description: question.description || '',
        type: question.type || 'text',
        required: Boolean(question.required),
        view_duration: question.view_duration,
        answer_duration: question.answer_duration,
        order: question.order_index || question.order || 0,
        // Include options for question types that support them
        options: (question.options || []).map(option => ({
            text: option.text || '',
            image_url: option.image_url || null,
            order: option.order || 0
        })).sort((a, b) => a.order - b.order) // Sort for consistent hashing
    };
    
    // Convert to JSON string for hashing
    const jsonString = JSON.stringify(normalizedQuestion);
    
    // Simple hash function (using a more robust one would be better in production)
    let hash = 0;
    for (let i = 0; i < jsonString.length; i++) {
        const char = jsonString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
}

const QUESTION_TYPES = {
    TEXT: 'text',
    TEXT_ONLY: 'text_only',
    MULTIPLE_CHOICE: 'multiple_choice',
    IMAGE_THEN_QUESTION: 'image_then_question',
    YES_NO: 'yes_no',
    RATING: 'rating',
    TABLE: 'table',
    IMAGE_ONLY: 'image_only',
    VIDEO_ONLY: 'video_only',
    AUDIO_ONLY: 'audio_only',
    VOICE_RECORDING: 'voice_recording',
    MULTI_SECTION: 'multi_section'
};

// Sortable Item Component for Questions/Pages
function SortableQuestion({ question, index, isActive, isMultiSection, expandedMultiSection, onNavigate, onToggleExpand, currentSectionIndex }) {
    const { t } = useTranslation()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: question.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const getQuestionTypeDisplay = (type) => {
        const typeMap = {
            text: t('Questionnaires.Builder.TypeTextResponse'),
            text_only: t('Questionnaires.Builder.TypeTextPage'),
            multiple_choice: t('Questionnaires.Builder.TypeMultipleChoice'),
            image_then_question: t('Questionnaires.Builder.TypeImageQuestion'),
            yes_no: t('Questionnaires.Builder.TypeYesNo'),
            rating: t('Questionnaires.Builder.TypeRating'),
            table: t('Questionnaires.Builder.TypeTableMatrix'),
            image_only: t('Questionnaires.Builder.TypeImageOnly'),
            video_only: t('Questionnaires.Builder.TypeVideoOnly'),
            audio_only: t('Questionnaires.Builder.TypeAudioPage'),
            voice_recording: t('Questionnaires.Builder.TypeVoiceRecording'),
            multi_section: t('Questionnaires.Builder.TypeMultiSection')
        };
        return typeMap[type] || type;
    };

    const getQuestionDisplayTitle = (question, index) => {
        if (question.type === QUESTION_TYPES.IMAGE_ONLY || question.type === QUESTION_TYPES.VIDEO_ONLY || question.type === QUESTION_TYPES.AUDIO_ONLY) {
            const label = question.type === QUESTION_TYPES.IMAGE_ONLY ? 'Image' : question.type === QUESTION_TYPES.AUDIO_ONLY ? 'Audio' : 'Video'
            return question.question_text || `${label} Page ${index + 1}`
        }
        return question.question_text || `Question ${index + 1}`
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`${isDragging ? 'opacity-50 shadow-lg' : ''}`}
        >
            {question.type === QUESTION_TYPES.MULTI_SECTION ? (
                <div className="space-y-1">
                    {/* Multi-section question header */}
                    <div 
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                            isActive && currentSectionIndex === -1
                            ? 'bg-iavisus-100 text-white' 
                            : 'bg-white hover:bg-gray-100'
                        }`}
                        onClick={() => {
                            onNavigate(index);
                        }}
                    >
                        <div className="flex items-center space-x-3">
                            <button
                                {...attributes}
                                {...listeners}
                                className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                            >
                                <Bars3Icon className="h-4 w-4" />
                            </button>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                isActive && currentSectionIndex === -1 ? 'bg-white text-iavisus-100' : 'bg-gray-200'
                            }`}>
                                {question.question_text ? <CheckIcon className="h-4 w-4" /> : index + 2}
                            </div>
                            <div className="flex-1 min-w-0">
                                <Text className={`font-medium truncate ${isActive && currentSectionIndex === -1 ? 'text-white' : ''}`}>
                                    {getQuestionDisplayTitle(question, index)}
                                </Text>
                                <Text className={`text-xs ${isActive && currentSectionIndex === -1 ? 'text-blue-100' : 'text-gray-500'}`}>
                                    {getQuestionTypeDisplay(question.type)}
                                </Text>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleExpand(question.id);
                                }}
                                className="p-1 hover:bg-gray-200 rounded"
                            >
                                {expandedMultiSection[question.id] ? (
                                    <ChevronUpIcon className="h-4 w-4" />
                                ) : (
                                    <ChevronDownIcon className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Expandable sections list */}
                    {expandedMultiSection[question.id] && question.sections && (
                        <div className="ml-6 space-y-1">
                            {question.sections.map((section, sectionIndex) => (
                                <div
                                    key={section.id}
                                    className={`p-2 rounded cursor-pointer transition-colors text-sm ${
                                        isActive && currentSectionIndex === sectionIndex
                                        ? 'bg-iavisus-100 text-white'
                                        : 'bg-gray-100 hover:bg-gray-200'
                                    }`}
                                    onClick={() => {
                                        onNavigate(index, sectionIndex);
                                    }}
                                >
                                    <div className="flex items-center space-x-2">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                            isActive && currentSectionIndex === sectionIndex 
                                            ? 'bg-white text-iavisus-100' : 'bg-gray-300 text-gray-600'
                                        }`}>
                                            {sectionIndex + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <Text className={`truncate ${isActive && currentSectionIndex === sectionIndex ? 'text-white' : 'text-gray-700'}`}>
                                                {section.question_text || `Section ${sectionIndex + 1}`}
                                            </Text>
                                            <Text className={`text-xs ${isActive && currentSectionIndex === sectionIndex ? 'text-blue-100' : 'text-gray-500'}`}>
                                                {getQuestionTypeDisplay(section.type)}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                /* Regular single-type question */
                <div 
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        isActive
                        ? 'bg-iavisus-100 text-white' 
                        : 'bg-white hover:bg-gray-100'
                    }`}
                    onClick={() => onNavigate(index)}
                >
                    <div className="flex items-center space-x-3">
                        <button
                            {...attributes}
                            {...listeners}
                            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                        >
                            <Bars3Icon className="h-4 w-4" />
                        </button>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            isActive ? 'bg-white text-iavisus-100' : 'bg-gray-200'
                        }`}>
                            {question.question_text ? <CheckIcon className="h-4 w-4" /> : index + 2}
                        </div>
                        <div className="flex-1 min-w-0">
                            <Text className={`font-medium truncate ${isActive ? 'text-white' : ''}`}>
                                {getQuestionDisplayTitle(question, index)}
                            </Text>
                            <Text className={`text-xs ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                                {getQuestionTypeDisplay(question.type)}
                            </Text>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Sortable Item Component for Sections
function SortableSection({ section, index, onEdit, onRemove }) {
    const { t } = useTranslation()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: section.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const getQuestionTypeDisplay = (type) => {
        const typeMap = {
            text: t('Questionnaires.Builder.TypeTextInput'),
            text_only: t('Questionnaires.Builder.TypeTextDisplay'),
            multiple_choice: t('Questionnaires.Builder.TypeMultipleChoice'),
            yes_no: t('Questionnaires.Builder.TypeYesNo'),
            rating: t('Questionnaires.Builder.TypeRating'),
            table: t('Questionnaires.Builder.TypeTableMatrix'),
            image_only: t('Questionnaires.Builder.TypeImageOnly'),
            video_only: t('Questionnaires.Builder.TypeVideoOnly'),
            voice_recording: t('Questionnaires.Builder.TypeVoiceRecording')
        };
        return typeMap[type] || type;
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`p-3 bg-white rounded-lg border border-gray-200 ${
                isDragging ? 'shadow-lg opacity-50' : ''
            }`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <button
                        {...attributes}
                        {...listeners}
                        className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                    >
                        <Bars3Icon className="h-5 w-5" />
                    </button>
                    <span className="w-6 h-6 bg-iavisus-100 text-white text-xs font-medium rounded-full flex items-center justify-center">
                        {index + 1}
                    </span>
                    <div>
                        <Text className="font-medium text-sm">
                            {section.question_text || `Section ${index + 1}`}
                        </Text>
                        <Text className="text-xs text-gray-500">
                            {getQuestionTypeDisplay(section.type)}
                        </Text>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => onEdit(index)}
                        className="p-1 text-iavisus-100 hover:bg-iavisus-50 rounded text-xs"
                    >
                        {t('Questionnaires.Builder.EditSection')}
                    </button>
                    <button
                        onClick={() => onRemove(section.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function ImprovedQuestionnaireBuilderModal({ isOpen, onClose, onSubmit, initialData = null, isEditing = false, projectUuid, onQuestionUpdate }) {
    const { t } = useTranslation()
    const [currentStep, setCurrentStep] = useState(0) // 0 = basic info, 1+ = questions
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [currentSectionIndex, setCurrentSectionIndex] = useState(-1) // -1 = no section selected, 0+ = section index
    const [expandedMultiSection, setExpandedMultiSection] = useState({}); // Track which multi-section questions are expanded
    const [formData, setFormData] = useState({
        title: initialData?.title || '',
        description: initialData?.description || '',
        anonymous_responses: initialData?.anonymous_responses || false,
        one_response_per_user: initialData?.one_response_per_user || true,
        is_timed: initialData?.is_timed || false,
        default_view_duration: initialData?.default_view_duration || 5,
        default_answer_duration: initialData?.default_answer_duration || 30,
        theme_text_color: (initialData?.theme_text_color === '#1f2937' || initialData?.theme_primary_color === '#1f2937' || initialData?.theme_primary_color === '#3b82f6')
            ? '#000000' // Convert old defaults (gray or indigo) to new default black  
            : (initialData?.theme_text_color || initialData?.theme_primary_color || '#000000'),
        theme_button_color: initialData?.theme_button_color || '#1e40af',
        theme_background_color: initialData?.theme_background_color || '#ffffff',
        record_screen: initialData?.record_screen || false,
        record_mouse: initialData?.record_mouse || false,
        record_camera: initialData?.record_camera || false,
        record_gaze: initialData?.record_gaze || false,
        questions: initialData?.questions || []
    })
    
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDragOver, setIsDragOver] = useState(false)
    const [uploadingMedia, setUploadingMedia] = useState(false)
    const [useChunkedUpload, setUseChunkedUpload] = useState(false)
    const [chunkedUploadFile, setChunkedUploadFile] = useState(null)
    const [autoSaving, setAutoSaving] = useState(false)
    const [lastSaved, setLastSaved] = useState(null)
    const [questionnaireUuid, setQuestionnaireUuid] = useState(initialData?.uuid || null)
    const [pendingChanges, setPendingChanges] = useState({
        questionnaire: false,
        questions: new Set()
    })
    const [savingQuestions, setSavingQuestions] = useState(new Set())
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [questionToDelete, setQuestionToDelete] = useState(null)
    const [deletingQuestion, setDeletingQuestion] = useState(false)
    const [originalQuestionHashes, setOriginalQuestionHashes] = useState({})
    const [currentQuestionHash, setCurrentQuestionHash] = useState(null) // Track current question's hash in real-time
    const [recordingSection, setRecordingSection] = useState(null) // section.id currently being recorded
    const [recordingTime, setRecordingTime] = useState(0) // elapsed seconds
    const fileInputRef = useRef(null)
    const autoSaveTimeoutRef = useRef(null)
    const periodicSaveIntervalRef = useRef(null)
    const chunkedUploaderRef = useRef(null)
    const mediaRecorderRef = useRef(null)
    const recordingChunksRef = useRef([])
    const recordingTimerRef = useRef(null)

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle section drag end
    const handleSectionDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const currentQuestion = getCurrentQuestion();
        if (!currentQuestion?.sections) return;

        const oldIndex = currentQuestion.sections.findIndex(section => section.id === active.id);
        const newIndex = currentQuestion.sections.findIndex(section => section.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const newSections = arrayMove(currentQuestion.sections, oldIndex, newIndex);
            await reorderSections(currentQuestion.id, newSections);
        }
    };

    // Handle question/page drag end
    const handleQuestionDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = formData.questions.findIndex(question => question.id === active.id);
        const newIndex = formData.questions.findIndex(question => question.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const newQuestions = arrayMove(formData.questions, oldIndex, newIndex);
            
            // Update the order field on each question based on their new position
            const questionsWithUpdatedOrder = newQuestions.map((question, index) => ({
                ...question,
                order_index: index,
                order: index
            }));
            
            // Update local state immediately for responsive UI
            setFormData(prev => ({
                ...prev,
                questions: questionsWithUpdatedOrder
            }));
            
            // Update current question index if the active question moved
            if (oldIndex === currentQuestionIndex) {
                setCurrentQuestionIndex(newIndex);
            } else if (oldIndex < currentQuestionIndex && newIndex >= currentQuestionIndex) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            } else if (oldIndex > currentQuestionIndex && newIndex <= currentQuestionIndex) {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
            }
            
            console.log('🔄 Saving question reorder:', {
                totalQuestions: questionsWithUpdatedOrder.length,
                newOrder: questionsWithUpdatedOrder.map(q => ({ id: q.id, order: q.order, title: q.question_text }))
            });
            
            // Save all affected questions with new order
            try {
                // Save the questionnaire which will include all questions with their new order
                await performAutoSave({ isManualSave: true });
                console.log('✅ Question order saved successfully');
            } catch (error) {
                console.error('❌ Failed to save question order:', error);
            }
        }
    };

    // Update form data when modal opens with initial data
    useEffect(() => {
        if (initialData && isOpen) {
            
            // Safely transform questions data to ensure all expected properties exist
            const safeQuestions = (initialData.questions || []).map((question, index) => {
                const transformedQuestion = transformBackendQuestionToFrontend(question, index)
                
                console.log('📥 Loading question from backend:', {
                    id: transformedQuestion.id,
                    uuid: transformedQuestion.uuid,
                    type: transformedQuestion.type,
                    sections: transformedQuestion.sections?.length || 0,
                    raw_view_duration: question.view_duration,
                    raw_answer_duration: question.answer_duration,
                    final_view_duration: transformedQuestion.view_duration,
                    final_answer_duration: transformedQuestion.answer_duration,
                    source: 'modal_reload'
                });
                
                return transformedQuestion
            })

            const newFormData = {
                title: initialData.title || '',
                description: initialData.description || '',
                anonymous_responses: initialData.allow_anonymous_responses || false,
                one_response_per_user: !initialData.allow_multiple_responses,
                is_timed: initialData.is_timed || false,
                default_view_duration: initialData.default_view_duration || 5,
                default_answer_duration: initialData.default_answer_duration || 30,
                theme_text_color: (initialData.theme_text_color === '#1f2937' || initialData.theme_color === '#1f2937' || initialData.theme_color === '#3b82f6') 
                    ? '#000000' // Convert old defaults (gray or indigo) to new default black
                    : (initialData.theme_text_color || initialData.theme_color || '#000000'),
                theme_button_color: initialData.theme_button_color || '#1e40af',
                theme_background_color: initialData.theme_background_color || '#ffffff',
                record_screen: initialData.record_screen || false,
                record_mouse: initialData.record_mouse || false,
                record_camera: initialData.record_camera || false,
                record_gaze: initialData.record_gaze || false,
                questions: safeQuestions
            };

            setFormData(newFormData);
            setQuestionnaireUuid(initialData.uuid || null);
            
            // Generate and store original hashes for change detection
            const hashes = {};
            safeQuestions.forEach(question => {
                if (question.uuid) { // Only hash questions that exist in the database
                    hashes[question.uuid] = generateQuestionHash(question);
                }
            });
            setOriginalQuestionHashes(hashes);
            
            // Set current question hash if we're on a question step
            if (safeQuestions.length > 0 && currentQuestionIndex < safeQuestions.length) {
                const currentHash = generateQuestionHash(safeQuestions[currentQuestionIndex]);
                setCurrentQuestionHash(currentHash);
            }
        }
    }, [initialData, isOpen, initialData?.uuid])
    
    // Reset questionnaire UUID for new questionnaires when modal opens
    useEffect(() => {
        if (isOpen) {
            // When modal opens, reset questionnaire UUID if this is a new questionnaire (no initialData.uuid)
            if (!initialData?.uuid) {
                setQuestionnaireUuid(null)
                setPendingChanges({ questionnaire: false, questions: new Set() })
                console.log('🔄 Reset questionnaire UUID and pending changes for new questionnaire creation')
            }
        }
    }, [isOpen, initialData?.uuid])

    // Note: Periodic auto-save disabled - now using hash-based save-on-question-switch
    // Questions are saved only when switching to another question and only if they have changed (hash comparison)

    // Cleanup all timers when component unmounts
    useEffect(() => {
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
            if (periodicSaveIntervalRef.current) {
                clearInterval(periodicSaveIntervalRef.current);
            }
        };
    }, []);

    if (!isOpen) return null

    const validateBasicInfo = () => {
        const newErrors = {}
        if (!formData.title.trim()) {
            newErrors.title = t('Questionnaires.Builder.ErrorTitleRequired')
        }
        if (formData.default_view_duration < 1 || formData.default_view_duration > 300) {
            newErrors.default_view_duration = 'View duration must be between 1-300 seconds'
        }
        if (formData.default_answer_duration < 5 || formData.default_answer_duration > 600) {
            newErrors.default_answer_duration = 'Answer duration must be between 5-600 seconds'
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const addQuestion = async () => {
        // Ensure basic info is filled before adding questions
        if (!formData.title || !formData.title.trim()) {
            setErrors({ title: t('Questionnaires.Builder.ErrorTitleBeforeQuestions') });
            setCurrentStep(0); // Force user to basic info step
            return;
        }
        
        // Clear any previous errors
        setErrors({});
        
        // Save current question before adding a new one
        if (currentStep === 1 && formData.questions[currentQuestionIndex]) {
            const currentQuestion = formData.questions[currentQuestionIndex];
            await saveIndividualQuestion(currentQuestion.id);
        }
        
        const newQuestion = {
            id: Date.now(),
            question_text: '',
            type: QUESTION_TYPES.MULTI_SECTION,
            required: true,
            options: [],
            view_duration: formData.default_view_duration,
            answer_duration: formData.default_answer_duration,
            order_index: formData.questions.length,
            display_settings: {
                layout_type: 'grid',
                items_per_row: 3
            },
            sections: []
        }
        
        const newIndex = formData.questions.length
        setFormData(prev => ({
            ...prev,
            questions: [...prev.questions, newQuestion]
        }))
        
        setCurrentQuestionIndex(newIndex)
        setCurrentStep(1) // Switch to questions step
    }

    const updateQuestion = (questionId, updates) => {
        let questionIndex = -1
        let updatedQuestion = null
        
        setFormData(prev => {
            const newFormData = {
                ...prev,
                questions: prev.questions.map((q, index) => {
                    if (q.id === questionId) {
                        questionIndex = index
                        updatedQuestion = { ...q, ...updates }
                        return updatedQuestion
                    }
                    return q
                })
            }
            return newFormData
        })
        
        // Update current question hash if this is the currently active question
        if (questionIndex === currentQuestionIndex && updatedQuestion) {
            const newHash = generateQuestionHash(updatedQuestion);
            setCurrentQuestionHash(newHash);
            console.log('🔄 Updated current question hash:', {
                questionId: updatedQuestion.id,
                newHash: newHash,
                field: Object.keys(updates)[0] // Log which field was updated
            });
        }
        
        // No auto-save here - only save on question exit
    }
    
    // Save only questionnaire-level data (title, description, settings)
    const saveQuestionnaireOnly = async () => {
        if (!projectUuid) return;
        
        // Validate required fields
        if (!formData.title || !formData.title.trim()) {
            console.error('❌ Cannot save questionnaire: title is required');
            throw new Error('Title is required');
        }
        
        try {
            const questionnairePayload = {
                title: formData.title.trim(),
                description: formData.description || '',
                status: 'draft',
                allow_anonymous_responses: formData.anonymous_responses,
                allow_multiple_responses: !formData.one_response_per_user,
                collect_email: false,
                show_progress_bar: true,
                randomize_questions: false,
                is_timed: formData.is_timed,
                default_view_duration: formData.default_view_duration,
                default_answer_duration: formData.default_answer_duration,
                auto_advance: false,
                record_screen: formData.record_screen || false,
                record_mouse: formData.record_mouse || false,
                record_camera: formData.record_camera || false,
                record_gaze: formData.record_gaze || false,
                theme_color: formData.theme_text_color || '#000000',
                theme_text_color: formData.theme_text_color || '#000000',
                theme_button_color: formData.theme_button_color || '#1e40af',
                theme_background_color: formData.theme_background_color || '#ffffff',
                welcome_message: null,
                thank_you_message: null
            };
            
            if (questionnaireUuid) {
                // Update existing questionnaire
                await updateQuestionnaire(projectUuid, questionnaireUuid, questionnairePayload);
                console.log('✅ Questionnaire-level data updated');
            } else {
                // Create new questionnaire
                const response = await createQuestionnaire(projectUuid, questionnairePayload);
                if (response && response.questionnaire && response.questionnaire.uuid) {
                    setQuestionnaireUuid(response.questionnaire.uuid);
                    console.log('✅ New questionnaire created:', response.questionnaire.uuid);
                }
            }
            
        } catch (error) {
            console.error('❌ Failed to save questionnaire:', error);
            throw error;
        }
    };

    // Initialize default rating options when question type changes to RATING
    const initializeDefaultRatingOptions = () => {
        const timestamp = Date.now();
        return [
            { id: `rating-1-${timestamp}`, text: 'Excellent', image_url: null },
            { id: `rating-2-${timestamp}`, text: 'Good', image_url: null },
            { id: `rating-3-${timestamp}`, text: 'Average', image_url: null },
            { id: `rating-4-${timestamp}`, text: 'Poor', image_url: null },
            { id: `rating-5-${timestamp}`, text: 'Very Poor', image_url: null }
        ]
    }
    
    // Initialize default table structure when section type changes to TABLE
    const initializeDefaultTableOptions = () => {
        const timestamp = Date.now();
        return {
            rows: [
                { id: `row-1-${timestamp}`, text: 'Row 1' },
                { id: `row-2-${timestamp}`, text: 'Row 2' },
                { id: `row-3-${timestamp}`, text: 'Row 3' }
            ],
            columns: [
                { id: `col-1-${timestamp}`, text: 'Option 1' },
                { id: `col-2-${timestamp}`, text: 'Option 2' },
                { id: `col-3-${timestamp}`, text: 'Option 3' },
                { id: `col-4-${timestamp}`, text: 'Option 4' },
                { id: `col-5-${timestamp}`, text: 'Option 5' }
            ],
            header_mode: 'headers' // Default to showing headers in header row
        }
    }

    // Handle question type change - disabled since all pages are multi-section
    const handleQuestionTypeChange = (questionId, newType) => {
        // Do nothing - all pages must remain as multi-section
        console.log('Question type change disabled - all pages use multi-section format');
    }

    // Generate gradient background from a single color
    const generateGradientBackground = (baseColor) => {
        // Normalize the color to ensure it has # prefix
        const normalizedColor = baseColor.startsWith('#') ? baseColor : `#${baseColor}`
        
        // If it's white or near-white, create a subtle gray gradient
        if (normalizedColor.toLowerCase() === '#ffffff' || normalizedColor.toLowerCase() === '#fff') {
            return 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)'
        }
        
        // Handle 3-character hex codes
        let hex = normalizedColor.replace('#', '')
        if (hex.length === 3) {
            hex = hex.split('').map(char => char + char).join('')
        }
        
        // Parse RGB values
        const r = parseInt(hex.substr(0, 2), 16)
        const g = parseInt(hex.substr(2, 2), 16)
        const b = parseInt(hex.substr(4, 2), 16)
        
        // Create a lighter version (20% lighter)
        const lighterR = Math.min(255, Math.floor(r + (255 - r) * 0.2))
        const lighterG = Math.min(255, Math.floor(g + (255 - g) * 0.2))
        const lighterB = Math.min(255, Math.floor(b + (255 - b) * 0.2))
        
        // Create a slightly darker version (10% darker) for depth
        const darkerR = Math.max(0, Math.floor(r * 0.9))
        const darkerG = Math.max(0, Math.floor(g * 0.9))
        const darkerB = Math.max(0, Math.floor(b * 0.9))
        
        const lighterColor = `rgb(${lighterR}, ${lighterG}, ${lighterB})`
        const darkerColor = `rgb(${darkerR}, ${darkerG}, ${darkerB})`
        
        return `linear-gradient(135deg, ${lighterColor} 0%, ${normalizedColor} 50%, ${darkerColor} 100%)`
    }

    // Get text color style with consistent opacity handling for gray colors
    const getTextColorStyle = (isSecondary = false) => {
        const color = formData.theme_text_color || formData.theme_color || '#000000'
        
        // Check if the color is gray-ish (low saturation)
        const normalizedColor = color.startsWith('#') ? color : `#${color}`
        let hex = normalizedColor.replace('#', '')
        if (hex.length === 3) {
            hex = hex.split('').map(char => char + char).join('')
        }
        
        const r = parseInt(hex.substr(0, 2), 16)
        const g = parseInt(hex.substr(2, 2), 16)
        const b = parseInt(hex.substr(4, 2), 16)
        
        // Calculate saturation to detect gray colors
        const max = Math.max(r, g, b)
        const min = Math.min(r, g, b)
        const saturation = max === 0 ? 0 : (max - min) / max
        
        // If it's a gray color (low saturation) or secondary text, apply reduced opacity
        const isGrayish = saturation < 0.2
        const opacity = (isGrayish || isSecondary) ? 0.7 : 1
        
        return { color, opacity }
    }


    // Generate CSS classes for multiple choice display layout
    const getMultipleChoiceLayoutClasses = (displaySettings) => {
        const layoutType = displaySettings?.layout_type || 'grid';
        const itemsPerRow = displaySettings?.items_per_row || 3;
        
        if (layoutType === 'vertical') {
            return 'space-y-4';
        }
        
        if (layoutType === 'horizontal') {
            return 'space-x-4 flex flex-wrap';
        }
        
        // Grid layout
        const gridClasses = {
            1: 'grid grid-cols-1 gap-6',
            2: 'grid grid-cols-1 md:grid-cols-2 gap-6',
            3: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6',
            4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
            5: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4',
            6: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4',
            7: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3',
            8: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3',
            9: 'grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2',
            10: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-10 gap-2'
        };
        
        return gridClasses[itemsPerRow] || gridClasses[3];
    }

    // Generate CSS classes for individual option container
    const getOptionContainerClasses = (displaySettings) => {
        const layoutType = displaySettings?.layout_type || 'grid';
        
        if (layoutType === 'horizontal') {
            return 'space-x-3 flex items-center';
        }
        
        // Both 'grid' and 'vertical' use vertical spacing within each option
        return 'space-y-3';
    }

    // Generate CSS classes for option images based on display settings
    const getImageClasses = (displaySettings) => {
        const layoutType = displaySettings?.layout_type || 'grid';
        const itemsPerRow = displaySettings?.items_per_row || 3;
        
        const baseClasses = 'object-cover rounded-lg border-2 border-gray-200 shadow-sm';
        
        if (layoutType === 'horizontal') {
            return `w-24 h-18 ${baseClasses}`; // Smaller for horizontal layout
        }
        
        if (layoutType === 'vertical') {
            return `w-full h-48 md:h-56 ${baseClasses}`; // Full width for vertical layout
        }
        
        // Grid layout - responsive sizing based on items per row
        const sizeClasses = {
            1: 'w-full h-64 md:h-72 lg:h-80', // Large for 1 column
            2: 'w-full h-56 md:h-64 lg:h-72', // Medium-large for 2 columns
            3: 'w-full h-48 md:h-56 lg:h-64', // Medium for 3 columns
            4: 'w-full h-40 md:h-48 lg:h-56'  // Smaller for 4 columns
        };
        
        return `${sizeClasses[itemsPerRow] || sizeClasses[3]} ${baseClasses}`;
    }

    // Get placeholder color using theme color with reduced opacity
    const getPlaceholderStyle = () => {
        const color = formData.theme_text_color
        
        // Convert to RGB for consistent opacity handling
        const normalizedColor = color.startsWith('#') ? color : `#${color}`
        let hex = normalizedColor.replace('#', '')
        if (hex.length === 3) {
            hex = hex.split('').map(char => char + char).join('')
        }
        
        const r = parseInt(hex.substr(0, 2), 16)
        const g = parseInt(hex.substr(2, 2), 16)
        const b = parseInt(hex.substr(4, 2), 16)
        
        // Always use 0.5 opacity for placeholders to ensure they're clearly distinguishable
        return `rgba(${r}, ${g}, ${b}, 0.5)`
    }

    // Generate dynamic CSS for placeholder styling
    const placeholderCSS = `
        .questionnaire-input::placeholder {
            color: ${getPlaceholderStyle()} !important;
        }
    `

    const handleDeleteQuestion = (questionId) => {
        const question = formData.questions.find(q => q.id === questionId);
        setQuestionToDelete(question);
        setShowDeleteModal(true);
    }

    const confirmDeleteQuestion = async () => {
        if (!questionToDelete) return;

        setDeletingQuestion(true);
        try {
            // If question has UUID (exists in database), delete from backend
            if (questionToDelete.uuid && projectUuid && questionnaireUuid) {
                await deleteQuestionAPI(projectUuid, questionnaireUuid, questionToDelete.uuid);
            }

            // Remove from frontend state
            setFormData(prev => ({
                ...prev,
                questions: prev.questions.filter(q => q.id !== questionToDelete.id)
            }))

            // Adjust current question index if needed
            const questionIndex = formData.questions.findIndex(q => q.id === questionToDelete.id);
            if (questionIndex === currentQuestionIndex && currentQuestionIndex > 0) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            } else if (questionIndex < currentQuestionIndex) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            }

            // Go back to basic info if no questions left
            if (formData.questions.length === 1) {
                setCurrentStep(0);
            }

            // Close modal
            setShowDeleteModal(false);
            setQuestionToDelete(null);

        } catch (error) {
            console.error('Failed to delete question:', error);
            // You might want to show an error toast here
        } finally {
            setDeletingQuestion(false);
        }
    }

    const cancelDeleteQuestion = () => {
        setShowDeleteModal(false);
        setQuestionToDelete(null);
    }

    const addOption = (questionId) => {
        const question = formData.questions.find(q => q.id === questionId);
        const currentOptions = question.options || [];
        const newOptions = [
            ...currentOptions,
            { 
                id: Date.now() + Math.random(), // Unique ID for React key
                text: '', 
                image_url: null 
            }
        ];
        
        updateQuestion(questionId, {
            options: newOptions
        })
    }

    const updateOption = (questionId, optionIndex, updates) => {
        const question = formData.questions.find(q => q.id === questionId)
        const updatedOptions = [...question.options]
        updatedOptions[optionIndex] = { ...updatedOptions[optionIndex], ...updates }
        
        updateQuestion(questionId, { options: updatedOptions })
    }

    const deleteOption = (questionId, optionIndex) => {
        const question = formData.questions.find(q => q.id === questionId)
        const updatedOptions = question.options.filter((_, index) => index !== optionIndex)
        updateQuestion(questionId, { options: updatedOptions })
    }
    
    // Section-specific option management functions
    const addSectionOption = (questionId, sectionId) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const currentOptions = section.options || []
        const newOptions = [
            ...currentOptions,
            { 
                id: Date.now() + Math.random(), // Unique ID for React key
                text: '', 
                image_url: null 
            }
        ]
        
        updateSection(questionId, sectionId, { options: newOptions })
    }
    
    const updateSectionOption = (questionId, sectionId, optionIndex, updates) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const updatedOptions = [...(section.options || [])]
        updatedOptions[optionIndex] = { ...updatedOptions[optionIndex], ...updates }
        
        updateSection(questionId, sectionId, { options: updatedOptions })
    }
    
    const deleteSectionOption = (questionId, sectionId, optionIndex) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const updatedOptions = (section.options || []).filter((_, index) => index !== optionIndex)
        updateSection(questionId, sectionId, { options: updatedOptions })
    }
    
    // Table management functions
    const addTableRow = (questionId, sectionId) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const currentRows = section.table_data?.rows || []
        const newRow = { id: `row-${Date.now()}`, text: `Row ${currentRows.length + 1}` }
        const updatedTableData = {
            ...section.table_data,
            rows: [...currentRows, newRow]
        }
        updateSection(questionId, sectionId, { table_data: updatedTableData })
    }
    
    const addTableColumn = (questionId, sectionId) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const currentColumns = section.table_data?.columns || []
        const newColumn = { id: `col-${Date.now()}`, text: `Column ${currentColumns.length + 1}` }
        const updatedTableData = {
            ...section.table_data,
            columns: [...currentColumns, newColumn]
        }
        updateSection(questionId, sectionId, { table_data: updatedTableData })
    }
    
    const updateTableRow = (questionId, sectionId, rowIndex, updates) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const updatedRows = [...(section.table_data?.rows || [])]
        updatedRows[rowIndex] = { ...updatedRows[rowIndex], ...updates }
        const updatedTableData = {
            ...section.table_data,
            rows: updatedRows
        }
        updateSection(questionId, sectionId, { table_data: updatedTableData })
    }
    
    const updateTableColumn = (questionId, sectionId, columnIndex, updates) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const updatedColumns = [...(section.table_data?.columns || [])]
        updatedColumns[columnIndex] = { ...updatedColumns[columnIndex], ...updates }
        const updatedTableData = {
            ...section.table_data,
            columns: updatedColumns
        }
        updateSection(questionId, sectionId, { table_data: updatedTableData })
    }
    
    const deleteTableRow = (questionId, sectionId, rowIndex) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const updatedRows = (section.table_data?.rows || []).filter((_, index) => index !== rowIndex)
        const updatedTableData = {
            ...section.table_data,
            rows: updatedRows
        }
        updateSection(questionId, sectionId, { table_data: updatedTableData })
    }
    
    const deleteTableColumn = (questionId, sectionId, columnIndex) => {
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        const updatedColumns = (section.table_data?.columns || []).filter((_, index) => index !== columnIndex)
        const updatedTableData = {
            ...section.table_data,
            columns: updatedColumns
        }
        updateSection(questionId, sectionId, { table_data: updatedTableData })
    }
    
    // Handle image upload for individual options in section IMAGE_CHOICE
    const handleSectionOptionImageUpload = async (questionId, sectionId, optionIndex, file) => {
        if (!file) return
        
        // Validate file type
        if (!isValidMediaFile(file, 'image_choice')) {
            setErrors({ media: 'Please select a valid image file' })
            return
        }
        
        // Clear any previous errors
        setErrors({})
        
        try {
            setUploadingMedia(true)
            
            const response = await uploadOptionMedia(
                projectUuid,
                questionnaireUuid,
                questionId, // This should work for sections too
                file
            )
            console.log('Section option image upload SUCCESS:', response)
            
            // Update the specific section option with the uploaded image URL
            updateSectionOption(questionId, sectionId, optionIndex, { 
                image_url: response.media_url
            })
            
        } catch (error) {
            console.error('Failed to upload section option image:', error)
            setErrors({ media: error.message || 'Failed to upload option image' })
        } finally {
            setUploadingMedia(false)
        }
    }
    
    // Handle section type change with special logic for RATING sections
    const handleSectionTypeChange = (questionId, sectionId, newType) => {
        const updates = { type: newType }
        
        // Initialize default options for RATING sections
        if (newType === QUESTION_TYPES.RATING) {
            const question = formData.questions.find(q => q.id === questionId)
            const section = question.sections.find(s => s.id === sectionId)
            
            if (!section.options || section.options.length === 0) {
                updates.options = initializeDefaultRatingOptions()
            }
        }
        // Initialize default table structure for TABLE sections
        else if (newType === QUESTION_TYPES.TABLE) {
            const question = formData.questions.find(q => q.id === questionId)
            const section = question.sections.find(s => s.id === sectionId)
            
            if (!section.table_data) {
                updates.table_data = initializeDefaultTableOptions()
            }
        }
        // Clear options for question types that don't need them
        else if (newType === QUESTION_TYPES.TEXT || newType === QUESTION_TYPES.YES_NO ||
                 newType === QUESTION_TYPES.TEXT_ONLY || newType === QUESTION_TYPES.IMAGE_ONLY ||
                 newType === QUESTION_TYPES.VIDEO_ONLY || newType === QUESTION_TYPES.AUDIO_ONLY ||
                 newType === QUESTION_TYPES.VOICE_RECORDING) {
            updates.options = []
            // Clear table data if switching away from table
            updates.table_data = null
        }
        // Ensure options array exists for question types that need them
        else if (newType === QUESTION_TYPES.MULTIPLE_CHOICE) {
            const question = formData.questions.find(q => q.id === questionId)
            const section = question.sections.find(s => s.id === sectionId)
            if (!section?.options) {
                updates.options = []
            }
        }
        
        updateSection(questionId, sectionId, updates)
    }
    
    // Map frontend types to backend types (for both questions and sections)
    const mapTypeToBackend = (frontendType) => {
        switch (frontendType) {
            case 'image_only':
                return 'image_choice'
            case 'video_only':
                return 'image_choice'
            case 'audio_only':
                return 'audio_only'
            case 'multi_section':
                return 'text' // Multi-section questions are treated as text questions with sections
            default:
                return frontendType
        }
    }
    
    // Keep old function name for backward compatibility
    const mapSectionTypeToBackend = mapTypeToBackend

    // Transform backend question data to frontend format
    const transformBackendQuestionToFrontend = (backendQuestion, index = 0) => {
        // Force all questions to be multi-section type
        let frontendType = 'multi_section'
        
        // Convert non-multi-section questions to multi-section format
        let transformedSections = []
        
        // If this was already a multi-section question, use its sections
        if (backendQuestion.type === 'multi_section' && backendQuestion.sections && backendQuestion.sections.length > 0) {
            transformedSections = backendQuestion.sections
        }
        // If this was a single question type, convert it to a section within multi-section
        else {
            // Create a single section from the original question
            const sectionType = backendQuestion.type === 'image_choice' && !backendQuestion.options?.length 
                ? (backendQuestion.media_url && (backendQuestion.media_url.includes('.mp4') || backendQuestion.media_url.includes('.webm') || backendQuestion.media_url.includes('.mov')) ? 'video_only' : 'image_only')
                : (backendQuestion.type || 'text')
                
            transformedSections = [{
                id: `converted-section-${backendQuestion.uuid || index}-${Date.now()}`,
                type: sectionType,
                title: backendQuestion.question_text || `Section 1`,
                question_text: backendQuestion.question_text || '',
                description: backendQuestion.description || '',
                options: backendQuestion.options || [],
                media_url: backendQuestion.media_url || backendQuestion.image_url || backendQuestion.video_url || null,
                order: 0,
                order_index: 0,
                required: backendQuestion.required || false,
                view_duration: backendQuestion.view_duration,
                answer_duration: backendQuestion.answer_duration,
                display_settings: backendQuestion.display_settings || { layout_type: 'grid', items_per_row: 3 }
            }]
        }

        // Process the sections with proper type mapping
        const finalSections = transformedSections.map((section, sectionIndex) => ({
            ...section,
            id: section.id || `section-${backendQuestion.uuid || index}-${sectionIndex}-${Date.now()}`,
            // Reverse map backend section types to frontend types  
            type: section.type === 'image_choice' ? (section.options?.length > 0 ? 'image_choice' : 'image_only') : 
                  section.type === 'text' ? 'text' : section.type,
            question_text: section.title || section.question_text || '',
            description: section.description || '',
            options: (section.options || []).map((option, optionIndex) => ({
                ...option,
                id: option.id || `section-${sectionIndex}-option-${optionIndex}-${Date.now()}`,
                text: option.option_text || option.text || '',
                image_url: option.image_url || null
            })),
            order_index: section.order_index ?? sectionIndex,
            display_settings: section.display_settings || { layout_type: 'grid', items_per_row: 3 },
            // Preserve media and timing fields
            media_url: section.media_url || null,
            image_url: section.image_url || null,
            video_url: section.video_url || null,
            view_duration: section.view_duration ?? null,
            answer_duration: section.answer_duration ?? null,
            // Preserve table data for table sections
            table_data: section.table_data || null
        }))

        return {
            id: backendQuestion.uuid || `temp_${index}`,
            uuid: backendQuestion.uuid,
            question_text: backendQuestion.question_text || '',
            description: backendQuestion.description || '',
            type: frontendType,
            required: Boolean(backendQuestion.required),
            options: (backendQuestion.options || []).map((option, optionIndex) => ({
                ...option,
                id: option.id || `option-${backendQuestion.uuid || index}-${optionIndex}-${Date.now()}`,
                text: option.option_text || option.text || '',
                image_url: option.image_url || null,
                thumbnail_url: option.image_thumbnail_url || null,
                medium_url: option.image_medium_url || null
            })),
            view_duration: backendQuestion.view_duration ?? null,
            answer_duration: backendQuestion.answer_duration ?? null,
            order_index: backendQuestion.order ?? index,
            order: backendQuestion.order ?? index,
            // Include sections for multi-section questions (now all questions are multi-section)
            sections: finalSections,
            // Media properties
            media_url: backendQuestion.media_url || backendQuestion.image_url || backendQuestion.video_url || null,
            media_filename: backendQuestion.media_filename || null,
            media_type: backendQuestion.media_type || null,
            media_id: backendQuestion.media_id || null,
            thumbnail_url: backendQuestion.thumbnail_url || backendQuestion.image_thumbnail_url || null,
            medium_url: backendQuestion.medium_url || backendQuestion.image_medium_url || null,
            // Additional properties
            skip_logic: backendQuestion.skip_logic || {},
            validation_rules: backendQuestion.validation_rules || {},
            display_settings: backendQuestion.display_settings || {}
        }
    }
    
    // Prepare sections for backend with type mapping
    const prepareSectionsForBackend = (sections) => {
        if (!sections || sections.length === 0) return []
        
        const result = sections.map((section, index) => {
            const preparedSection = {
                ...section,
                type: mapSectionTypeToBackend(section.type),
                title: section.question_text || section.title || `Section ${index + 1}`,
                description: section.description || '',
                order: section.order !== undefined ? section.order : (section.order_index !== undefined ? section.order_index : index),
                options: (section.options || []).filter(option => 
                    option && option.text && option.text.trim().length > 0
                ),
                display_settings: section.display_settings || { layout_type: 'grid', items_per_row: 3 }
            };

            if (section.media_url) preparedSection.media_url = section.media_url;
            if (section.image_url) preparedSection.image_url = section.image_url;
            if (section.video_url) preparedSection.video_url = section.video_url;

            // Include table data for table sections
            if (section.table_data) preparedSection.table_data = section.table_data;

            if (section.view_duration !== null && section.view_duration !== undefined) {
                preparedSection.view_duration = section.view_duration;
            }
            if (section.answer_duration !== null && section.answer_duration !== undefined) {
                preparedSection.answer_duration = section.answer_duration;
            }

            return preparedSection;
        });
        
        return result;
    }
    
    // Handle media upload for IMAGE_ONLY and VIDEO_ONLY sections
    const handleSectionMediaUpload = async (questionId, sectionId, file) => {
        if (!file) return
        
        const question = formData.questions.find(q => q.id === questionId)
        const section = question.sections.find(s => s.id === sectionId)
        
        // Validate file type
        if (!isValidMediaFile(file, section.type)) {
            const expectedType = section.type === 'image_only' ? 'image' : section.type === 'audio_only' ? 'audio' : 'video'
            setErrors({ media: `Please select a valid ${expectedType} file` })
            return
        }
        
        // Clear any previous errors
        setErrors({})
        
        try {
            setUploadingMedia(true)
            
            // For sections, we'll use the main question's media upload endpoint
            // But first ensure we have a saved question to attach media to
            let questionUuid = question.uuid
            if (!questionUuid) {
                // Need to save the questionnaire/question first
                const autoSaveResult = await performAutoSave()
                if (!autoSaveResult.success) {
                    throw new Error('Failed to save questionnaire before uploading section media')
                }
                // Get the updated question UUID
                const updatedQuestion = formData.questions.find(q => q.id === questionId)
                questionUuid = updatedQuestion.uuid
            }
            
            // Determine media type based on section type
            const mediaType = section.type === 'image_only' ? 'image' : section.type === 'audio_only' ? 'audio' : 'video'
            
            const response = await uploadQuestionMedia(
                projectUuid,
                questionnaireUuid,
                questionUuid,
                file,
                mediaType
            )
            console.log('Section media upload SUCCESS:', response)
            
            // Update the section with the uploaded media URL
            updateSection(questionId, sectionId, { 
                media_url: response.media_url
            })
            
        } catch (error) {
            console.error('Failed to upload section media:', error)
            setErrors({ media: error.message || 'Failed to upload section media' })
        } finally {
            setUploadingMedia(false)
        }
    }

    const startSectionRecording = async (questionId, sectionId) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            recordingChunksRef.current = []

            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/webm')
                ? 'audio/webm'
                : 'audio/ogg'

            const recorder = new MediaRecorder(stream, { mimeType })
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) recordingChunksRef.current.push(e.data)
            }
            recorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop())
                const blob = new Blob(recordingChunksRef.current, { type: mimeType })
                const ext = mimeType.includes('webm') ? 'webm' : 'ogg'
                const file = new File([blob], `recording_${Date.now()}.${ext}`, { type: mimeType })
                await handleSectionMediaUpload(questionId, sectionId, file)
                setRecordingSection(null)
                setRecordingTime(0)
            }

            recorder.start(250)
            mediaRecorderRef.current = recorder
            setRecordingSection(sectionId)
            setRecordingTime(0)
            recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000)
        } catch (err) {
            setErrors({ media: 'Microphone access denied or unavailable' })
        }
    }

    const stopSectionRecording = () => {
        clearInterval(recordingTimerRef.current)
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop()
        }
    }

    const handleSubmit = async () => {
        if (currentStep === 0) {
            if (validateBasicInfo()) {
                // If no questions exist, create first one
                if (formData.questions.length === 0) {
                    addQuestion()
                } else {
                    setCurrentStep(1)
                }
            }
            return
        }

        setIsSubmitting(true)
        try {
            // Sanitize all input data
            const sanitizedData = {
                title: sanitizeUserInput(formData.title),
                description: sanitizeUserInput(formData.description),
                anonymous_responses: formData.anonymous_responses,
                one_response_per_user: formData.one_response_per_user,
                is_timed: formData.is_timed,
                default_view_duration: formData.default_view_duration,
                default_answer_duration: formData.default_answer_duration,
                record_screen: formData.record_screen || false,
                record_mouse: formData.record_mouse || false,
                record_camera: formData.record_camera || false,
                record_gaze: formData.record_gaze || false,
                theme_text_color: formData.theme_text_color,
                theme_button_color: formData.theme_button_color,
                theme_background_color: formData.theme_background_color,
                questions: formData.questions.map(q => ({
                    ...q,
                    question_text: sanitizeUserInput(q.question_text),
                    options: q.options.map(opt => ({
                        ...opt,
                        text: sanitizeUserInput(opt.text)
                    }))
                }))
            }


            // Save current question before submitting
            if (currentStep === 1 && formData.questions[currentQuestionIndex]) {
                const currentQuestion = formData.questions[currentQuestionIndex];
                await saveIndividualQuestion(currentQuestion.id);
            }

            // Update formData with sanitized data for questionnaire-only save
            setFormData(prev => ({
                ...prev,
                title: sanitizedData.title,
                description: sanitizedData.description,
                anonymous_responses: sanitizedData.anonymous_responses,
                one_response_per_user: sanitizedData.one_response_per_user,
                is_timed: sanitizedData.is_timed,
                default_view_duration: sanitizedData.default_view_duration,
                default_answer_duration: sanitizedData.default_answer_duration,
                record_screen: sanitizedData.record_screen,
                record_mouse: sanitizedData.record_mouse,
                record_camera: sanitizedData.record_camera,
                record_gaze: sanitizedData.record_gaze,
                theme_text_color: sanitizedData.theme_text_color,
                theme_button_color: sanitizedData.theme_button_color,
                theme_background_color: sanitizedData.theme_background_color
            }));

            // Check and save current question if it has changes (hash comparison) before closing
            if (currentStep === 1 && formData.questions[currentQuestionIndex]) {
                const currentQuestion = formData.questions[currentQuestionIndex];
                const currentHash = generateQuestionHash(currentQuestion);
                const originalHash = currentQuestion.uuid ? originalQuestionHashes[currentQuestion.uuid] : null;
                const hasChanged = !currentQuestion.uuid || currentHash !== originalHash;
                
                console.log('🔍 Final question hash check before close:', {
                    questionId: currentQuestion.id,
                    uuid: currentQuestion.uuid || 'NEW',
                    hasChanged,
                    action: hasChanged ? 'SAVE' : 'SKIP'
                });
                
                if (hasChanged) {
                    console.log('💾 Saving final question before close...');
                    await saveIndividualQuestion(currentQuestion.id);
                } else {
                    console.log('⏭️ Skipping final question save - no changes detected');
                }
            }
            
            // Save questionnaire-level data only
            await saveQuestionnaireOnly();
            
            console.log('✅ Manual questionnaire update completed');
            
            onClose()
        } catch (error) {
            console.error('Failed to create questionnaire:', error)
            setErrors({ submit: error.message })
        } finally {
            setIsSubmitting(false)
        }
    }

    const getCurrentQuestion = () => {
        return formData.questions[currentQuestionIndex] || null
    }

    const getQuestionTypeDisplay = (type) => {
        const typeMap = {
            [QUESTION_TYPES.TEXT]: t('Questionnaires.Builder.TypeTextResponse'),
            [QUESTION_TYPES.TEXT_ONLY]: t('Questionnaires.Builder.TypeTextPage'),
            [QUESTION_TYPES.MULTIPLE_CHOICE]: t('Questionnaires.Builder.TypeMultipleChoice'),
            [QUESTION_TYPES.IMAGE_THEN_QUESTION]: t('Questionnaires.Builder.TypeImageQuestion'),
            [QUESTION_TYPES.YES_NO]: t('Questionnaires.Builder.TypeYesNo'),
            [QUESTION_TYPES.RATING]: t('Questionnaires.Builder.TypeRating'),
            [QUESTION_TYPES.IMAGE_ONLY]: t('Questionnaires.Builder.TypeImageOnly'),
            [QUESTION_TYPES.VIDEO_ONLY]: t('Questionnaires.Builder.TypeVideoOnly'),
            [QUESTION_TYPES.AUDIO_ONLY]: t('Questionnaires.Builder.TypeAudioPage'),
            [QUESTION_TYPES.VOICE_RECORDING]: t('Questionnaires.Builder.TypeVoiceRecording'),
            [QUESTION_TYPES.MULTI_SECTION]: t('Questionnaires.Builder.TypeMultiSectionPageDisplay')
        }
        return typeMap[type] || type
    }

    // Section Management Functions
    const addSection = (questionId, sectionType = QUESTION_TYPES.TEXT) => {
        const newSection = {
            id: Date.now() + Math.random(),
            type: sectionType,
            question_text: '',
            description: '',
            options: [],
            order_index: 0,
            display_settings: { layout_type: 'grid', items_per_row: 3 }
        }
        
        console.log('➕ ADDING SECTION:', {
            questionId,
            sectionType,
            newSection,
            currentSections: getCurrentQuestion()?.sections?.length || 0
        });
        
        updateQuestion(questionId, {
            sections: [...(getCurrentQuestion()?.sections || []), newSection]
        })
        
        // DEBUG: Check what the question looks like after adding section
        setTimeout(() => {
            const question = formData.questions.find(q => q.id === questionId);
            console.log('✅ AFTER ADDING SECTION - Question now has:', {
                questionId,
                sectionsCount: question?.sections?.length || 0,
                sections: question?.sections
            });
        }, 100);
    }

    const removeSection = (questionId, sectionId) => {
        const currentSections = getCurrentQuestion()?.sections || []
        const updatedSections = currentSections.filter(section => section.id !== sectionId)
        updateQuestion(questionId, { sections: updatedSections })
        
        // Reset section selection if we removed the current section
        if (currentSectionIndex >= updatedSections.length) {
            setCurrentSectionIndex(-1)
        }
    }

    const updateSection = (questionId, sectionId, updates) => {
        const currentSections = getCurrentQuestion()?.sections || []
        const updatedSections = currentSections.map(section => 
            section.id === sectionId ? { ...section, ...updates } : section
        )
        updateQuestion(questionId, { sections: updatedSections })
    }

    const reorderSections = async (questionId, newOrder) => {
        // Update the order field on each section based on their new position
        const sectionsWithUpdatedOrder = newOrder.map((section, index) => ({
            ...section,
            order: index
        }));
        
        // Update local state immediately for responsive UI
        updateQuestion(questionId, { sections: sectionsWithUpdatedOrder });
        
        // Trigger backend save to persist the new order
        console.log('🔄 Saving section reorder:', {
            questionId,
            sectionsCount: sectionsWithUpdatedOrder.length,
            newOrder: sectionsWithUpdatedOrder.map(s => ({ id: s.id, order: s.order, title: s.question_text || s.title }))
        });
        
        try {
            // Save the question with the new section order
            await saveIndividualQuestion(questionId);
            console.log('✅ Section order saved successfully');
        } catch (error) {
            console.error('❌ Failed to save section order:', error);
            // You could add user feedback here if needed
        }
    }

    const getCurrentSection = () => {
        const question = getCurrentQuestion()
        if (!question?.sections || currentSectionIndex < 0 || currentSectionIndex >= question.sections.length) {
            return null
        }
        return question.sections[currentSectionIndex]
    }

    const navigateToSection = (sectionIndex) => {
        setCurrentSectionIndex(sectionIndex)
    }

    // Save individual question - handles both new and existing questions
    const saveIndividualQuestion = async (questionId) => {
        if (!projectUuid) return;
        
        const currentQuestion = formData.questions.find(q => q.id === questionId);
        if (!currentQuestion) return;

        // DEBUG: Log what question data we have when saving
        console.log('💾 SAVING QUESTION - Current question data:', {
            id: currentQuestion.id,
            type: currentQuestion.type,
            question_text: currentQuestion.question_text,
            sections: currentQuestion.sections,
            sectionsCount: currentQuestion.sections?.length || 0
        });
        
        // Prevent multiple saves of the same question
        if (savingQuestions.has(questionId)) {
            console.log('⏳ Question save already in progress:', questionId);
            return;
        }
        
        setSavingQuestions(prev => new Set(prev).add(questionId));
        
        // Ensure questionnaire exists before saving question
        let currentQuestionnaireUuid = questionnaireUuid;
        if (!currentQuestionnaireUuid) {
            // Don't create questionnaire if we don't have basic required info
            if (!formData.title || !formData.title.trim()) {
                console.log('⚠️ Cannot save question: questionnaire title is required');
                setSavingQuestions(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(questionId);
                    return newSet;
                });
                return;
            }
            
            console.log('⚠️ No questionnaire UUID, creating questionnaire first');
            const response = await createQuestionnaire(projectUuid, {
                title: formData.title.trim(),
                description: formData.description || '',
                status: 'draft',
                allow_anonymous_responses: formData.anonymous_responses,
                allow_multiple_responses: !formData.one_response_per_user,
                collect_email: false,
                show_progress_bar: true,
                randomize_questions: false,
                is_timed: formData.is_timed,
                default_view_duration: formData.default_view_duration,
                default_answer_duration: formData.default_answer_duration,
                auto_advance: false,
                theme_color: formData.theme_text_color || '#000000',
                theme_text_color: formData.theme_text_color || '#000000',
                theme_button_color: formData.theme_button_color || '#1e40af',
                theme_background_color: formData.theme_background_color || '#ffffff'
            });
            
            if (response && response.questionnaire && response.questionnaire.uuid) {
                currentQuestionnaireUuid = response.questionnaire.uuid;
                setQuestionnaireUuid(currentQuestionnaireUuid);
                console.log('✅ Created questionnaire for question save:', currentQuestionnaireUuid);
            } else {
                console.error('❌ Failed to create questionnaire, cannot save question');
                return;
            }
        }
        
        try {
            // Apply question text fallback for special question types
            let questionText = currentQuestion.question_text;
            if (!questionText?.trim()) {
                if (currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY) {
                    questionText = 'Image Question';
                } else if (currentQuestion.type === QUESTION_TYPES.VIDEO_ONLY) {
                    questionText = 'Video Question';
                } else if (currentQuestion.type === QUESTION_TYPES.AUDIO_ONLY) {
                    questionText = 'Audio Question';
                } else if (currentQuestion.type === QUESTION_TYPES.MULTI_SECTION) {
                    questionText = 'Multi-section Page';
                } else {
                    questionText = 'Untitled Question';
                }
            }
            
            const payload = {
                question_text: questionText,
                description: currentQuestion.description || '',
                type: mapTypeToBackend(currentQuestion.type),
                required: currentQuestion.required || false,
                options: (currentQuestion.options || []).filter(option => 
                    option && option.text && option.text.trim().length > 0
                ),
                order: currentQuestion.order_index || currentQuestion.order || 0,
                sections: prepareSectionsForBackend(currentQuestion.sections || [])
            };

            // DEBUG: Log multi-section payload details
            if (currentQuestion.type === QUESTION_TYPES.MULTI_SECTION) {
                console.log('🔍 DEBUG - Multi-section question payload:', {
                    question_text: payload.question_text,
                    type: payload.type,
                    originalType: currentQuestion.type,
                    sectionsCount: payload.sections.length,
                    sectionsData: payload.sections,
                    currentQuestionSections: currentQuestion.sections,
                    fullPayload: payload
                });
                
                // Also log what's being sent to the API
                console.log('🚀 SENDING TO BACKEND API:', JSON.stringify(payload, null, 2));
            }
            
            // Only include timing fields if they have valid values
            if (currentQuestion.view_duration !== null && currentQuestion.view_duration !== undefined) {
                payload.view_duration = currentQuestion.view_duration;
            }
            if (currentQuestion.answer_duration !== null && currentQuestion.answer_duration !== undefined) {
                payload.answer_duration = currentQuestion.answer_duration;
            }
            
            if (currentQuestion.uuid) {
                // Update existing question
                await updateQuestionAPI(projectUuid, currentQuestionnaireUuid, currentQuestion.uuid, payload);
                console.log('✅ Updated existing question:', { questionId, question_text: currentQuestion.question_text });
            } else {
                // Create new question
                const response = await createQuestion(projectUuid, currentQuestionnaireUuid, payload);
                
                // Update the question with the new UUID from the backend
                if (response && response.question && response.question.uuid) {
                    setFormData(prev => ({
                        ...prev,
                        questions: prev.questions.map(q => 
                            q.id === questionId 
                                ? { ...q, uuid: response.question.uuid }
                                : q
                        )
                    }));
                    console.log('✅ Created new question:', { questionId, uuid: response.question.uuid, question_text: currentQuestion.question_text });
                }
            }
            
        } catch (error) {
            console.error('❌ Failed to save individual question:', { questionId, error });
        } finally {
            setSavingQuestions(prev => {
                const newSet = new Set(prev);
                newSet.delete(questionId);
                return newSet;
            });
        }
    };

    // Unified save system that always returns complete questionnaire
    // This replaces both auto-save and manual save to ensure consistent behavior
    const performUnifiedSave = async (currentFormData, options = {}) => {
        const { isManualSave = false, triggerQuestionnaireListUpdate = false } = options;
        
        if (!projectUuid || (!isManualSave && !pendingChanges.questionnaire && pendingChanges.questions.size === 0)) {
            return { questionnaireUuid, questionUuids: {} }
        }

        try {
            setAutoSaving(true)
            let resultQuestionnaireUuid = questionnaireUuid
            const resultQuestionUuids = {}
            
            if (questionnaireUuid) {
                // Update questionnaire if it has changes
                if (pendingChanges.questionnaire) {
                    await updateQuestionnaire(projectUuid, questionnaireUuid, {
                        title: currentFormData.title,
                        description: currentFormData.description,
                        status: 'draft',
                        allow_anonymous_responses: currentFormData.anonymous_responses,
                        allow_multiple_responses: !currentFormData.one_response_per_user,
                        collect_email: false,
                        show_progress_bar: true,
                        randomize_questions: false,
                        is_timed: currentFormData.is_timed,
                        default_view_duration: currentFormData.default_view_duration,
                        default_answer_duration: currentFormData.default_answer_duration,
                        auto_advance: false,
                        record_screen: currentFormData.record_screen || false,
                        record_mouse: currentFormData.record_mouse || false,
                        record_camera: currentFormData.record_camera || false,
                record_gaze: currentFormData.record_gaze || false,
                        theme_color: currentFormData.theme_text_color,
                        theme_text_color: currentFormData.theme_text_color,
                        theme_button_color: currentFormData.theme_button_color,
                        theme_background_color: currentFormData.theme_background_color,
                        welcome_message: null,
                        thank_you_message: null
                    })
                }

                // Batch update only changed questions using hash comparison
                const changedQuestions = currentFormData.questions.filter((question) => {
                    // Always include new questions (no UUID)
                    if (!question.uuid) {
                        return true;
                    }
                    
                    // For existing questions, compare hashes
                    const currentHash = generateQuestionHash(question);
                    const originalHash = originalQuestionHashes[question.uuid];
                    
                    // If we don't have an original hash, consider it changed (safety fallback)
                    if (!originalHash) {
                        return true;
                    }
                    
                    // Only include if hash has changed
                    return currentHash !== originalHash;
                })

                if (changedQuestions.length > 0) {
                    console.log('💾 Auto-saving with HASH-BASED change detection:', {
                        totalQuestions: currentFormData.questions.length,
                        changedQuestions: changedQuestions.length,
                        skippedQuestions: currentFormData.questions.length - changedQuestions.length,
                        changedQuestionDetails: changedQuestions.map(q => {
                            const isNew = !q.uuid;
                            const currentHash = isNew ? 'new' : generateQuestionHash(q);
                            const originalHash = isNew ? 'new' : originalQuestionHashes[q.uuid];
                            
                            return {
                                id: q.id,
                                uuid: q.uuid || 'NEW',
                                type: q.type,
                                isNew,
                                hashChanged: isNew || currentHash !== originalHash,
                                currentHash: currentHash,
                                originalHash: originalHash,
                                optionsCount: (q.options || []).length
                            };
                        })
                    });

                    // Process questions in smaller batches to avoid overwhelming the server
                    const BATCH_SIZE = 3
                    for (let i = 0; i < changedQuestions.length; i += BATCH_SIZE) {
                        const batch = changedQuestions.slice(i, i + BATCH_SIZE)
                        
                        // Process batch in parallel, but batches sequentially
                        const batchPromises = batch.map(async (question) => {
                            
                            if (question.uuid) {
                                // Map frontend question types to backend accepted types
                                let backendQuestionType = mapTypeToBackend(question.type);
                                
                                // Filter options to only include ones with text (requirement: options must have text to be saved)
                                const filteredOptions = (question.options || []).filter(option => 
                                    option && option.text && option.text.trim().length > 0
                                );
                                
                                // For special question types, use placeholder text if question_text is empty
                                let questionText = question.question_text;
                                if (!questionText?.trim()) {
                                    if (question.type === QUESTION_TYPES.IMAGE_ONLY) {
                                        questionText = 'Image Question';
                                    } else if (question.type === QUESTION_TYPES.VIDEO_ONLY) {
                                        questionText = 'Video Question';
                                    } else if (question.type === QUESTION_TYPES.MULTI_SECTION) {
                                        questionText = 'Multi-section Page';
                                    } else {
                                        questionText = 'Untitled Question';
                                    }
                                }

                                // Update existing question
                                const updatePayload = {
                                    question_text: questionText,
                                    type: backendQuestionType,
                                    required: question.required || false,
                                    options: filteredOptions, // Use filtered options that have text
                                    order: question.order_index || question.order || 0,
                                    sections: prepareSectionsForBackend(question.sections || [])
                                };
                                
                                // DEBUG: Log auto-save payload
                                console.log('🔍 DEBUG - Auto-save question payload:', {
                                    questionUuid: question.uuid,
                                    questionText: question.question_text,
                                    questionType: backendQuestionType,
                                    originalOptionsCount: (question.options || []).length,
                                    filteredOptionsCount: filteredOptions.length,
                                    filteredOptionsData: filteredOptions,
                                    updatePayload
                                });
                                
                                
                                // Only include timing fields if they have valid values
                                if (question.view_duration !== null && question.view_duration !== undefined) {
                                    updatePayload.view_duration = question.view_duration;
                                }
                                if (question.answer_duration !== null && question.answer_duration !== undefined) {
                                    updatePayload.answer_duration = question.answer_duration;
                                }
                                
                                const response = await updateQuestionAPI(projectUuid, questionnaireUuid, question.uuid, updatePayload)
                                
                                // Note: Individual question sync removed - full questionnaire refresh handles all synchronization
                            } else {
                                // Map frontend question types to backend accepted types
                                let backendQuestionType = mapTypeToBackend(question.type);
                        
                        // Filter options to only include ones with text (requirement: options must have text to be saved)
                        const filteredOptions = (question.options || []).filter(option => 
                            option && option.text && option.text.trim().length > 0
                        );
                        
                        // For special question types, use placeholder text if question_text is empty
                        let questionText = question.question_text;
                        if (!questionText?.trim()) {
                            if (question.type === QUESTION_TYPES.IMAGE_ONLY) {
                                questionText = 'Image Question';
                            } else if (question.type === QUESTION_TYPES.VIDEO_ONLY) {
                                questionText = 'Video Question';
                            } else if (question.type === QUESTION_TYPES.MULTI_SECTION) {
                                questionText = 'Multi-section Page';
                            } else {
                                questionText = 'Untitled Question';
                            }
                        }

                        // Create new question
                        const createPayload = {
                            question_text: questionText,
                            type: backendQuestionType,
                            required: question.required || false,
                            options: filteredOptions, // Use filtered options that have text
                            order: question.order_index || question.order || 0,
                            sections: prepareSectionsForBackend(question.sections || [])
                        };
                        
                        // Only include timing fields if they have valid values
                        if (question.view_duration !== null && question.view_duration !== undefined) {
                            createPayload.view_duration = question.view_duration;
                        }
                        if (question.answer_duration !== null && question.answer_duration !== undefined) {
                            createPayload.answer_duration = question.answer_duration;
                        }
                        
                        const newQuestion = await createQuestion(projectUuid, questionnaireUuid, createPayload)
                        
                        // Store in result for return - full refresh will sync all state
                        resultQuestionUuids[question.id] = newQuestion.question.uuid
                            }
                        })
                        
                        // Wait for batch to complete
                        await Promise.all(batchPromises)
                    }
                }
            } else {
                // Create new questionnaire - use current form data
                const questionnairePayload = {
                    title: currentFormData.title,
                    description: currentFormData.description,
                    status: 'draft',
                    allow_anonymous_responses: currentFormData.anonymous_responses,
                    allow_multiple_responses: !currentFormData.one_response_per_user,
                    collect_email: false,
                    show_progress_bar: true,
                    randomize_questions: false,
                    is_timed: currentFormData.is_timed,
                    default_view_duration: currentFormData.default_view_duration,
                    default_answer_duration: currentFormData.default_answer_duration,
                    auto_advance: false,
                    record_screen: currentFormData.record_screen || false,
                    record_mouse: currentFormData.record_mouse || false,
                    record_camera: currentFormData.record_camera || false,
                record_gaze: currentFormData.record_gaze || false,
                    theme_color: currentFormData.theme_text_color || '#000000',
                    theme_text_color: currentFormData.theme_text_color || '#000000',
                    theme_button_color: currentFormData.theme_button_color || '#1e40af',
                    theme_background_color: currentFormData.theme_background_color || '#ffffff',
                    welcome_message: null,
                    thank_you_message: null
                }

                let newQuestionnaire;
                try {
                    newQuestionnaire = await createQuestionnaire(projectUuid, questionnairePayload)
                    
                    if (!newQuestionnaire || !newQuestionnaire.questionnaire?.uuid) {
                        console.error('Invalid questionnaire response:', newQuestionnaire)
                        throw new Error('Failed to create questionnaire - invalid response')
                    }
                } catch (createError) {
                    console.error('Questionnaire creation FAILED:', createError)
                    throw createError
                }
                
                resultQuestionnaireUuid = newQuestionnaire.questionnaire.uuid
                setQuestionnaireUuid(newQuestionnaire.questionnaire.uuid) // Set UUID immediately for API calls
                
                // Now create questions individually using current form data
                for (const question of currentFormData.questions) {
                    try {
                        // Map frontend question types to backend accepted types
                        let backendQuestionType = mapTypeToBackend(question.type);
                        
                        // Filter options to only include ones with text (requirement: options must have text to be saved)
                        const filteredOptions = (question.options || []).filter(option => 
                            option && option.text && option.text.trim().length > 0
                        );
                        
                        // For special question types, use placeholder text if question_text is empty
                        let questionText = question.question_text;
                        if (!questionText?.trim()) {
                            if (question.type === QUESTION_TYPES.IMAGE_ONLY) {
                                questionText = 'Image Question';
                            } else if (question.type === QUESTION_TYPES.VIDEO_ONLY) {
                                questionText = 'Video Question';
                            } else if (question.type === QUESTION_TYPES.MULTI_SECTION) {
                                questionText = 'Multi-section Page';
                            } else {
                                questionText = 'Untitled Question';
                            }
                        }

                        const newQuestion = await createQuestion(projectUuid, resultQuestionnaireUuid, {
                            question_text: questionText,
                            type: backendQuestionType,
                            required: question.required || false,
                            options: filteredOptions, // Use filtered options that have text
                            order: question.order_index || question.order || 0,
                            sections: prepareSectionsForBackend(question.sections || [])
                        })
                        
                        // Store in result - full refresh will sync all state
                        resultQuestionUuids[question.id] = newQuestion.question.uuid
                    } catch (questionError) {
                        console.error('Failed to create question:', questionError)
                        throw questionError
                    }
                }
            }
            
            // After successful save, get the complete fresh questionnaire from backend
            let completeQuestionnaire = null;
            if (resultQuestionnaireUuid) {
                try {
                    const freshQuestionnaireResponse = await getQuestionnaire(projectUuid, resultQuestionnaireUuid);
                    completeQuestionnaire = freshQuestionnaireResponse.questionnaire;
                    
                    // Only refresh local state for manual saves, not auto-saves to preserve user's work-in-progress
                    if (isManualSave) {
                        await refreshQuestionnaireFromBackendWithUuid(resultQuestionnaireUuid);
                        console.log('🔄 Complete questionnaire refreshed after manual save');
                    }
                    
                    // Update parent questionnaire list only for manual saves
                    // Auto-saves should not trigger parent list updates to avoid clearing user's work-in-progress
                    if (isManualSave && onQuestionUpdate && completeQuestionnaire) {
                        onQuestionUpdate(completeQuestionnaire);
                        console.log('📋 Parent questionnaire list updated with fresh data');
                    }
                    
                } catch (refreshError) {
                    console.error('⚠️ Failed to refresh questionnaire after save:', refreshError);
                    // Don't fail the save if refresh fails, but log it
                }
            }
            
            // Update hashes after successful save
            setOriginalQuestionHashes(prev => {
                const updatedHashes = { ...prev };
                
                // Update hashes for questions that were saved
                currentFormData.questions.forEach(question => {
                    if (question.uuid) {
                        updatedHashes[question.uuid] = generateQuestionHash(question);
                    }
                    // For new questions, use the returned UUID
                    if (!question.uuid && resultQuestionUuids[question.id]) {
                        updatedHashes[resultQuestionUuids[question.id]] = generateQuestionHash(question);
                    }
                });
                
                return updatedHashes;
            });
            
            return { 
                questionnaireUuid: resultQuestionnaireUuid, 
                questionUuids: resultQuestionUuids, 
                completeQuestionnaire 
            }
            
        } catch (error) {
            console.error('Auto-save error:', error)
            throw error
        } finally {
            setAutoSaving(false)
        }
    }

    // Legacy function for backwards compatibility - uses current state via functional update
    const performAutoSave = async (options = {}) => {
        return new Promise((resolve, reject) => {
            setFormData(currentFormData => {
                performUnifiedSave(currentFormData, options)
                    .then(resolve)
                    .catch(reject);
                return currentFormData; // Return unchanged to avoid re-render
            });
        });
    }

    // Fetch and sync complete questionnaire from backend to ensure full synchronization
    const refreshQuestionnaireFromBackend = async () => {
        if (!questionnaireUuid || !projectUuid) {
            console.log('⚠️ Cannot refresh questionnaire: missing UUID or project UUID');
            return;
        }

        try {
            console.log('🔄 Refreshing complete questionnaire from backend...', {
                questionnaireUuid,
                projectUuid
            });

            const response = await getQuestionnaire(projectUuid, questionnaireUuid);
            
            if (response && response.questionnaire) {
                const backendQuestionnaire = response.questionnaire;
                
                console.log('✅ Received complete questionnaire from backend:', {
                    title: backendQuestionnaire.title,
                    questionsCount: (backendQuestionnaire.questions || []).length,
                    questionsWithOptions: (backendQuestionnaire.questions || []).map(q => ({
                        id: q.uuid,
                        type: q.type,
                        optionsCount: (q.options || []).length
                    }))
                });

                // Transform backend data to match frontend format
                const transformedQuestions = (backendQuestionnaire.questions || []).map((backendQuestion, index) => {
                    const transformed = transformBackendQuestionToFrontend(backendQuestion, index);
                    console.log('📥 Loading question from backend:', {
                        id: transformed.id,
                        type: transformed.type,
                        sections: transformed.sections?.length || 0,
                        source: 'data_refresh'
                    });
                    return transformed;
                });

                // Update complete frontend state with backend data
                setFormData(prev => ({
                    ...prev,
                    title: backendQuestionnaire.title || prev.title,
                    description: backendQuestionnaire.description || prev.description,
                    anonymous_responses: backendQuestionnaire.allow_anonymous_responses ?? prev.anonymous_responses,
                    one_response_per_user: !backendQuestionnaire.allow_multiple_responses ?? prev.one_response_per_user,
                    is_timed: backendQuestionnaire.is_timed ?? prev.is_timed,
                    default_view_duration: backendQuestionnaire.default_view_duration ?? prev.default_view_duration,
                    default_answer_duration: backendQuestionnaire.default_answer_duration ?? prev.default_answer_duration,
                    theme_text_color: backendQuestionnaire.theme_text_color || prev.theme_text_color,
                    theme_button_color: backendQuestionnaire.theme_button_color || prev.theme_button_color,
                    theme_background_color: backendQuestionnaire.theme_background_color || prev.theme_background_color,
                    questions: transformedQuestions
                }));

                console.log('✅ Frontend state updated with complete backend questionnaire');
                
            } else {
                console.error('❌ Invalid questionnaire response from backend:', response);
            }
            
        } catch (error) {
            console.error('❌ Failed to refresh questionnaire from backend:', {
                error: error,
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                questionnaireUuid,
                projectUuid,
                apiCall: `getQuestionnaire(${projectUuid}, ${questionnaireUuid})`
            });
            throw error;
        }
    };

    // Version that accepts UUID parameter to avoid stale state issues during auto-save
    const refreshQuestionnaireFromBackendWithUuid = async (uuid) => {
        if (!uuid || !projectUuid) {
            console.log('⚠️ Cannot refresh questionnaire: missing UUID or project UUID');
            return;
        }

        try {
            const response = await getQuestionnaire(projectUuid, uuid);
            
            if (response) {
                const backendQuestionnaire = response;

                // Transform backend data to match frontend format
                const transformedQuestions = (backendQuestionnaire.questions || []).map((backendQuestion, index) => {
                    const transformed = transformBackendQuestionToFrontend(backendQuestion, index);
                    console.log('📥 Loading question from backend:', {
                        id: transformed.id,
                        type: transformed.type,
                        sections: transformed.sections?.length || 0,
                        source: 'data_refresh'
                    });
                    return transformed;
                });

                // Update complete frontend state with backend data
                setFormData(prev => ({
                    ...prev,
                    title: backendQuestionnaire.title || prev.title,
                    description: backendQuestionnaire.description || prev.description,
                    is_timed: backendQuestionnaire.is_timed ?? prev.is_timed,
                    default_view_duration: backendQuestionnaire.default_view_duration ?? prev.default_view_duration,
                    default_answer_duration: backendQuestionnaire.default_answer_duration ?? prev.default_answer_duration,
                    theme_text_color: backendQuestionnaire.theme_text_color || prev.theme_text_color,
                    theme_button_color: backendQuestionnaire.theme_button_color || prev.theme_button_color,
                    theme_background_color: backendQuestionnaire.theme_background_color || prev.theme_background_color,
                    questions: transformedQuestions
                }));

                // Update the UUID state if it's different
                if (uuid !== questionnaireUuid) {
                    setQuestionnaireUuid(uuid);
                }

                console.log('✅ Frontend state updated with complete backend questionnaire');
                
                // Update hashes after refresh from backend
                const refreshedHashes = {};
                transformedQuestions.forEach(question => {
                    if (question.uuid) {
                        refreshedHashes[question.uuid] = generateQuestionHash(question);
                    }
                });
                setOriginalQuestionHashes(refreshedHashes);
                
            } else {
                console.error('❌ Invalid questionnaire response from backend:', response);
            }
            
        } catch (error) {
            console.error('❌ Failed to refresh questionnaire from backend:', {
                error: error,
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                questionnaireUuid: uuid,
                projectUuid,
                apiCall: `getQuestionnaire(${projectUuid}, ${uuid})`
            });
            throw error;
        }
    };


    // Navigate to a question with hash-based change detection
    const navigateToQuestion = async (index) => {
        // Ensure basic info is filled before navigating to questions
        if (!formData.title || !formData.title.trim()) {
            setErrors({ title: t('Questionnaires.Builder.ErrorTitleFirst') });
            setCurrentStep(0); // Force user to basic info step
            return;
        }
        
        // Clear any previous errors
        setErrors({});
        
        // Check and save current question only if it has changed (hash comparison)
        if (currentStep === 1 && formData.questions[currentQuestionIndex]) {
            const currentQuestion = formData.questions[currentQuestionIndex];
            
            // Generate current hash for the question being left
            const currentHash = generateQuestionHash(currentQuestion);
            const originalHash = currentQuestion.uuid ? originalQuestionHashes[currentQuestion.uuid] : null;
            
            // Only save if hash has changed or it's a new question (no UUID)
            const hasChanged = !currentQuestion.uuid || currentHash !== originalHash;
            
            console.log('🔍 Question switch hash check:', {
                questionId: currentQuestion.id,
                uuid: currentQuestion.uuid || 'NEW',
                hasChanged,
                currentHash: currentHash,
                originalHash: originalHash || 'none',
                action: hasChanged ? 'SAVE' : 'SKIP'
            });
            
            if (hasChanged) {
                console.log('💾 Saving question due to hash change...');
                await saveIndividualQuestion(currentQuestion.id);
                
                // Update the hash after successful save
                if (currentQuestion.uuid) {
                    setOriginalQuestionHashes(prev => ({
                        ...prev,
                        [currentQuestion.uuid]: currentHash
                    }));
                }
            } else {
                console.log('⏭️ Skipping save - no changes detected');
            }
        }
        
        setCurrentStep(1);
        setCurrentQuestionIndex(index);
        
        // Set the current question hash for the new question
        if (formData.questions[index]) {
            const newCurrentHash = generateQuestionHash(formData.questions[index]);
            setCurrentQuestionHash(newCurrentHash);
        }
    }

    // Update form data (questionnaire-level only)
    const updateFormData = (updates) => {
        setFormData(prev => ({ ...prev, ...updates }))
    }

    const getQuestionDisplayTitle = (question, index) => {
        if (question.type === QUESTION_TYPES.IMAGE_ONLY || question.type === QUESTION_TYPES.VIDEO_ONLY || question.type === QUESTION_TYPES.AUDIO_ONLY) {
            const label = question.type === QUESTION_TYPES.IMAGE_ONLY ? 'Image' : question.type === QUESTION_TYPES.AUDIO_ONLY ? 'Audio' : 'Video'
            return question.question_text || `${label} Page ${index + 1}`
        }
        return question.question_text || `Question ${index + 1}`
    }

    // Drag and drop handlers for media upload
    const handleDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleFileDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)

        const files = Array.from(e.dataTransfer.files)
        
        if (files.length > 0) {
            const droppedFile = files[0]
            handleMediaUpload(droppedFile)
        }
    }

    // Validate file type based on question type
    const isValidMediaFile = (file, questionType) => {
        const extension = file.name.split('.').pop().toLowerCase()

        if (questionType === QUESTION_TYPES.IMAGE_ONLY ||
            questionType === QUESTION_TYPES.IMAGE_THEN_QUESTION) {
            const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
            return imageTypes.includes(extension)
        } else if (questionType === QUESTION_TYPES.VIDEO_ONLY) {
            const videoTypes = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v', '3gp', 'ogv']
            return videoTypes.includes(extension)
        } else if (questionType === QUESTION_TYPES.AUDIO_ONLY) {
            const audioTypes = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'opus', 'webm']
            return audioTypes.includes(extension)
        }

        return false
    }

    // Handle media file upload with auto-save
    const handleMediaUpload = async (file) => {
        const currentQuestion = getCurrentQuestion()
        if (!currentQuestion) return

        // Validate file type
        if (!isValidMediaFile(file, currentQuestion.type)) {
            const expectedType = (
                currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY ||
                currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION
            ) ? 'image' : 'video'
            setErrors({ media: `Please select a valid ${expectedType} file` })
            return
        }

        // Check if file needs chunked uploading (larger than 50MB)
        const maxRegularSize = 50 * 1024 * 1024 // 50MB in bytes - matches backend chunked uploader
        const useChunked = file.size > maxRegularSize
        
        if (useChunked) {
            // Clear any previous errors
            setErrors({})
            
            // Ensure questionnaire and question exist before chunked upload
            const performChunkedUpload = async () => {
                try {
                    // Validate minimum requirements before proceeding
                    if (!formData.title?.trim()) {
                        throw new Error('Please add a questionnaire title before uploading media')
                    }
                    // For image_only and video_only questions, the media IS the content, so question text is not required
                    const isMediaOnlyQuestion = currentQuestion?.type === QUESTION_TYPES.IMAGE_ONLY ||
                                                currentQuestion?.type === QUESTION_TYPES.VIDEO_ONLY ||
                                                currentQuestion?.type === QUESTION_TYPES.AUDIO_ONLY;
                    if (!isMediaOnlyQuestion && !currentQuestion?.question_text?.trim()) {
                        throw new Error('Please add question text before uploading media')
                    }
                    
                    // Ensure questionnaire exists
                    let workingQuestionnaireUuid = questionnaireUuid
                    if (!workingQuestionnaireUuid) {
                        const { questionnaireUuid: resultQuestionnaireUuid } = await performAutoSave()
                        if (!resultQuestionnaireUuid) {
                            throw new Error('Failed to create questionnaire for chunked upload')
                        }
                        workingQuestionnaireUuid = resultQuestionnaireUuid
                        setQuestionnaireUuid(workingQuestionnaireUuid)
                    }
                    
                    // Ensure question exists 
                    let currentQuestionUuid = currentQuestion?.uuid
                    if (!currentQuestionUuid) {
                        // Directly create the question in the database
                        console.log('Creating question directly for chunked upload - question ID:', currentQuestion?.id)
                        
                        // Prepare question text
                        let questionText = currentQuestion?.question_text;
                        if (!questionText?.trim()) {
                            if (currentQuestion?.type === QUESTION_TYPES.IMAGE_ONLY) {
                                questionText = 'Image Question';
                            } else if (currentQuestion?.type === QUESTION_TYPES.VIDEO_ONLY) {
                                questionText = 'Video Question';
                            } else if (currentQuestion?.type === QUESTION_TYPES.MULTI_SECTION) {
                                questionText = 'Multi-section Page';
                            } else {
                                questionText = 'Untitled Question';
                            }
                        }
                        
                        // Map frontend type to backend type
                        let backendQuestionType = mapTypeToBackend(currentQuestion?.type || 'text');
                        
                        const createPayload = {
                            question_text: questionText,
                            type: backendQuestionType,
                            required: currentQuestion?.required || false,
                            options: [],
                            order: currentQuestion?.order_index || currentQuestion?.order || 0,
                            sections: prepareSectionsForBackend(currentQuestion?.sections || [])
                        };
                        
                        const newQuestion = await createQuestion(projectUuid, workingQuestionnaireUuid, createPayload)
                        currentQuestionUuid = newQuestion.question.uuid
                        
                        if (!currentQuestionUuid) {
                            throw new Error('Failed to create question for chunked upload - no UUID returned')
                        }
                        
                        // Update the question with its UUID
                        updateQuestion(currentQuestion.id, { uuid: currentQuestionUuid })
                    }
                    
                    // Start chunked upload
                    setUseChunkedUpload(true)
                    setChunkedUploadFile(file)
                    
                } catch (error) {
                    console.error('Failed to setup chunked upload:', error)
                    setErrors({ media: error.message || 'Failed to setup chunked upload. Please try again.' })
                }
            }
            
            performChunkedUpload()
            return
        }

        // Clear any previous errors
        setErrors({})
        setUploadingMedia(true)

        try {
            // Clean filename - extract just the filename from any path
            const cleanFilename = file.name.split('/').pop().split('\\').pop()
            
            // Validate minimum requirements before proceeding
            if (!formData.title?.trim()) {
                throw new Error('Please add a questionnaire title before uploading media')
            }

            // For image_only, video_only, and audio_only questions, the media IS the content, so question text is not required
            const isMediaOnlyQuestion = currentQuestion?.type === QUESTION_TYPES.IMAGE_ONLY ||
                                        currentQuestion?.type === QUESTION_TYPES.VIDEO_ONLY ||
                                        currentQuestion?.type === QUESTION_TYPES.AUDIO_ONLY;
            if (!isMediaOnlyQuestion && !currentQuestion?.question_text?.trim()) {
                throw new Error('Please add question text before uploading media')
            }
            
            // Ensure questionnaire exists
            let workingQuestionnaireUuid = questionnaireUuid
            if (!workingQuestionnaireUuid) {
                try {
                    console.log('Creating questionnaire via auto-save...')
                    const autoSaveResult = await performAutoSave()
                    console.log('Auto-save result:', autoSaveResult)
                    workingQuestionnaireUuid = autoSaveResult.questionnaireUuid
                    
                    if (!workingQuestionnaireUuid) {
                        throw new Error('Auto-save did not create questionnaire UUID')
                    }
                    console.log('Questionnaire created successfully with UUID:', workingQuestionnaireUuid)
                } catch (saveError) {
                    console.error('Failed to create questionnaire:', saveError)
                    throw new Error(`Failed to create questionnaire: ${saveError.message}`)
                }
            }

            // Ensure current question has a UUID
            let currentQuestionUuid = currentQuestion?.uuid
            if (!currentQuestionUuid) {
                try {
                    // Directly create the question in the database
                    console.log('Creating question directly for upload - question ID:', currentQuestion?.id)
                    
                    // Prepare question text
                    let questionText = currentQuestion?.question_text;
                    if (!questionText?.trim()) {
                        if (currentQuestion?.type === QUESTION_TYPES.IMAGE_ONLY) {
                            questionText = 'Image Question';
                        } else if (currentQuestion?.type === QUESTION_TYPES.VIDEO_ONLY) {
                            questionText = 'Video Question';
                        } else if (currentQuestion?.type === QUESTION_TYPES.MULTI_SECTION) {
                            questionText = 'Multi-section Page';
                        } else {
                            questionText = 'Untitled Question';
                        }
                    }
                    
                    // Map frontend type to backend type
                    let backendQuestionType = mapTypeToBackend(currentQuestion?.type || 'text');
                    
                    // Create the question directly
                    const createPayload = {
                        question_text: questionText,
                        type: backendQuestionType,
                        required: currentQuestion?.required || false,
                        options: [], // No options for image_only/video_only questions
                        order: currentQuestion?.order_index || currentQuestion?.order || 0,
                        sections: prepareSectionsForBackend(currentQuestion?.sections || [])
                    };
                    
                    const newQuestion = await createQuestion(projectUuid, workingQuestionnaireUuid, createPayload)
                    currentQuestionUuid = newQuestion.question.uuid
                    
                    if (!currentQuestionUuid) {
                        throw new Error('Failed to create question - no UUID returned from API')
                    }
                    
                    console.log('Question created successfully with UUID:', currentQuestionUuid)
                    
                    // Update the question state with the new UUID
                    updateQuestion(currentQuestion.id, { uuid: currentQuestionUuid })
                    
                } catch (saveError) {
                    console.error('Failed to create question directly:', saveError)
                    throw new Error(`Failed to create question: ${saveError.message}`)
                }
            }

            // Final validation before upload
            if (!workingQuestionnaireUuid || !currentQuestionUuid) {
                throw new Error('Unable to create questionnaire or question for media upload')
            }

            // Upload media to Digital Ocean
            const mediaType = (
                currentQuestion?.type === QUESTION_TYPES.IMAGE_ONLY ||
                currentQuestion?.type === QUESTION_TYPES.IMAGE_THEN_QUESTION
            ) ? 'image' : 'video'
            
            let response;
            console.log('CORS Debug - Upload attempt details:', {
                endpoint: 'uploadQuestionMedia',
                projectUuid,
                workingQuestionnaireUuid, 
                currentQuestionUuid,
                mediaType,
                fileSize: file.size,
                fileName: file.name,
                apiBase: process.env.REACT_APP_API_BASE_URL
            })
            try {
                response = await uploadQuestionMedia(
                    projectUuid, 
                    workingQuestionnaireUuid, 
                    currentQuestionUuid, 
                    file, 
                    mediaType
                )
                console.log('Media upload SUCCESS:', response)
            } catch (uploadError) {
                console.error('Media upload FAILED:', uploadError)
                console.error('Upload error response:', uploadError.response?.data)
                console.error('CORS Debug - Regular upload error details:', {
                    message: uploadError.message,
                    name: uploadError.name,
                    code: uploadError.code,
                    response: uploadError.response,
                    config: uploadError.config,
                    stack: uploadError.stack,
                    isCorsError: uploadError.message.toLowerCase().includes('cors') || 
                               uploadError.code === 'ERR_BLOCKED_BY_CORS' ||
                               (uploadError.response?.status === 0 && uploadError.request)
                })
                console.error('Upload error status:', uploadError.response?.status)
                // If this is a CORS error, provide specific guidance
                if (uploadError.message.toLowerCase().includes('cors') || 
                    uploadError.code === 'ERR_BLOCKED_BY_CORS' ||
                    (uploadError.response?.status === 0 && uploadError.request)) {
                    throw new Error('CORS Error: Video upload blocked by browser security. Please check your browser console for details and contact support if the issue persists.')
                }
                
                throw new Error(`Upload failed: ${uploadError.response?.data?.error || uploadError.message}`)
            }
            
            // Update question with signed URLs from successful upload
            try {
                if (currentQuestion?.id) {
                    updateQuestion(currentQuestion.id, { 
                    media_url: response.media_url,
                    media_filename: cleanFilename,
                    media_type: response.media_type,
                    media_id: response.media_id,
                    thumbnail_url: response.thumbnail_url,
                    medium_url: response.medium_url,
                    // Clear any pending upload data
                    pending_media_file: null,
                    pending_media_type: null
                    })
                }
            } catch (updateError) {
                console.warn('Upload succeeded but question update failed:', updateError)
                // Don't throw here since upload was successful - just log the warning
            }
            
        } catch (error) {
            console.error('Failed to upload media:', error)
            setErrors({ media: error.message || 'Failed to upload media file. Please try again.' })
        } finally {
            setUploadingMedia(false)
        }
    }

    // Handle file input change for click upload
    const handleFileInputChange = (e) => {
        const selectedFile = e.target.files[0]
        if (selectedFile) {
            handleMediaUpload(selectedFile)
        }
    }

    // Handle image upload for individual options in IMAGE_CHOICE questions
    const handleOptionImageUpload = async (questionId, optionIndex, file) => {
        if (!file) return

        // Validate file type
        if (!isValidMediaFile(file, 'image_choice')) {
            setErrors({ media: 'Please select a valid image file' })
            return
        }

        // Clear any previous errors
        setErrors({})
        
        try {
            // Use the proper questionnaire UUID state, not formData.uuid
            let workingQuestionnaireUuid = questionnaireUuid
            let backendQuestionId = null
            let backendOptionId = null

            // Check if we need to create/save questionnaire first
            if (!workingQuestionnaireUuid) {
                console.log('Creating questionnaire for option image upload via auto-save...')
                const { questionnaireUuid: resultQuestionnaireUuid } = await performAutoSave()
                if (!resultQuestionnaireUuid) {
                    throw new Error('Failed to create questionnaire for option image upload')
                }
                workingQuestionnaireUuid = resultQuestionnaireUuid
                setQuestionnaireUuid(workingQuestionnaireUuid)
            }

            // Now find the question and option IDs
            const question = formData.questions.find(q => q.id === questionId)
            if (!question) {
                throw new Error('Question not found for option image upload')
            }

            // Check if question is saved (has UUID)
            if (!question.uuid) {
                console.log('Question not saved yet, saving question first...')
                await saveIndividualQuestion(questionId)
                // After saving, the question should have a UUID
                const updatedQuestion = formData.questions.find(q => q.id === questionId)
                if (!updatedQuestion?.uuid) {
                    throw new Error('Failed to save question for option image upload')
                }
                backendQuestionId = updatedQuestion.uuid
            } else {
                backendQuestionId = question.uuid
            }

            // Get the backend option ID
            const option = question.options?.[optionIndex]
            if (!option?.id || typeof option.id === 'string' && (option.id.includes('temp') || option.id.includes('Date.now'))) {
                console.log('Option not saved yet, saving question to get backend option IDs...')
                await saveIndividualQuestion(questionId)
                
                // After saving question, refresh questionnaire data to get updated option IDs
                try {
                    const refreshedData = await getQuestionnaire(projectUuid, workingQuestionnaireUuid)
                    if (refreshedData?.questionnaire?.questions) {
                        const refreshedQuestion = refreshedData.questionnaire.questions.find(
                            q => q.uuid === backendQuestionId
                        )
                        if (refreshedQuestion?.options?.[optionIndex]?.id) {
                            backendOptionId = refreshedQuestion.options[optionIndex].id
                        } else {
                            throw new Error('Option ID not found even after saving question')
                        }
                    } else {
                        throw new Error('Failed to refresh questionnaire data')
                    }
                } catch (refreshError) {
                    throw new Error(`Failed to get option ID after saving: ${refreshError.message}`)
                }
            } else {
                backendOptionId = option.id
            }

            if (!workingQuestionnaireUuid || !backendQuestionId || !backendOptionId) {
                throw new Error('Unable to get required backend IDs for option image upload')
            }

            // Upload image using the option media endpoint
            console.log('Uploading option image:', file.name, { workingQuestionnaireUuid, backendQuestionId, backendOptionId })
            const response = await uploadOptionMedia(
                projectUuid,
                workingQuestionnaireUuid,
                backendQuestionId,
                backendOptionId,
                file
            )
            console.log('Option image upload SUCCESS:', response)
            
            // Update the specific option with the uploaded image URL
            updateOption(questionId, optionIndex, { 
                image_url: response.media_url
            })
            
        } catch (error) {
            console.error('Failed to upload option image:', error)
            setErrors({ media: error.message || 'Failed to upload option image. Please try again.' })
        }
    }

    // Callbacks for chunked upload
    const handleChunkedUploadComplete = (mediaData) => {
        console.log('Chunked upload completed:', mediaData)
        
        // Update question with media data from chunked upload
        const currentQuestion = getCurrentQuestion()
        if (currentQuestion?.id) {
            updateQuestion(currentQuestion.id, { 
                media_url: mediaData.media_url,
                media_filename: mediaData.media_filename,
                media_type: mediaData.media_type,
                media_id: mediaData.media_id,
                thumbnail_url: mediaData.thumbnail_url,
                // Clear chunked upload state
                pending_media_file: null,
                pending_media_type: null
            })
        }

        // Reset chunked upload state
        setUseChunkedUpload(false)
        setChunkedUploadFile(null)
    }

    const handleChunkedUploadError = (error) => {
        console.error('Chunked upload failed:', error)
        setErrors({ media: error })
        
        // Reset chunked upload state
        setUseChunkedUpload(false) 
        setChunkedUploadFile(null)
    }

    const handleChunkedUploadProgress = (percentage) => {
        // Update progress if needed - for now just log
        console.log(`Chunked upload progress: ${percentage}%`)
    }

    const currentQuestion = getCurrentQuestion()

    return (
        <>
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            {/* Dynamic CSS for placeholder colors */}
            <style>{placeholderCSS}</style>
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="w-full h-full bg-white flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <Dialog.Title className="text-xl font-semibold text-gray-900">
                                {isEditing ? t('Questionnaires.Builder.TitleEdit') : t('Questionnaires.Builder.TitleCreate')}
                            </Dialog.Title>
                            <div className="flex items-center space-x-4 mt-1">
                                <Text className="text-gray-600">
                                    {currentStep === 0 ? t('Questionnaires.Builder.LabelBasicInformation') : t('Questionnaires.Builder.StepQuestionOf', { current: currentQuestionIndex + 1, total: formData.questions.length })}
                                </Text>
                                {autoSaving && (
                                    <div className="flex items-center space-x-2 text-sm text-blue-600">
                                        <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                                        <span>{t('Questionnaires.Builder.StatusSaving')}</span>
                                    </div>
                                )}
                                {lastSaved && !autoSaving && (
                                    <Text className="text-green-600 text-sm">
                                        Saved {lastSaved.toLocaleTimeString()}
                                    </Text>
                                )}
                                {errors.autosave && (
                                    <Text className="text-red-600 text-sm">
                                        {errors.autosave}
                                    </Text>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="flex-1 flex overflow-hidden">
                        {/* Left Panel - Questions Progress */}
                        <div className="w-80 border-r bg-gray-50 p-6 overflow-y-auto">
                            <div className="space-y-4">
                                <div 
                                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                        currentStep === 0 
                                        ? 'bg-iavisus-100 text-white' 
                                        : 'bg-white hover:bg-gray-100'
                                    }`}
                                    onClick={() => setCurrentStep(0)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                            currentStep === 0 ? 'bg-white text-iavisus-100' : 'bg-gray-200'
                                        }`}>
                                            {formData.title ? <CheckIcon className="h-4 w-4" /> : '1'}
                                        </div>
                                        <div>
                                            <Text className={`font-medium ${currentStep === 0 ? 'text-white' : ''}`}>
                                                {t('Questionnaires.Builder.StepBasicInfo')}
                                            </Text>
                                            <Text className={`text-xs ${currentStep === 0 ? 'text-blue-100' : 'text-gray-500'}`}>
                                                {t('Questionnaires.Builder.StepBasicInfoSub')}
                                            </Text>
                                        </div>
                                    </div>
                                </div>

                                <DndContext 
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleQuestionDragEnd}
                                >
                                    <SortableContext 
                                        items={formData.questions.map(q => q.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {formData.questions.map((question, index) => (
                                            <SortableQuestion
                                                key={question.id}
                                                question={question}
                                                index={index}
                                                isActive={currentStep === 1 && currentQuestionIndex === index}
                                                isMultiSection={question.type === QUESTION_TYPES.MULTI_SECTION}
                                                expandedMultiSection={expandedMultiSection}
                                                onNavigate={navigateToQuestion}
                                                onToggleExpand={(questionId) => {
                                                    setExpandedMultiSection(prev => ({
                                                        ...prev,
                                                        [questionId]: !prev[questionId]
                                                    }));
                                                }}
                                                currentSectionIndex={currentSectionIndex}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>

                                <button
                                    onClick={addQuestion}
                                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg transition-colors flex items-center justify-center space-x-2 text-gray-600 hover:border-iavisus-100 hover:text-iavisus-100"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                    <span>{t('Questionnaires.Builder.BtnAddPage')}</span>
                                </button>
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className="flex-1 flex">
                            {/* Center Panel - Main Editing Area */}
                            <div 
                                className="flex-1 overflow-y-auto"
                                style={currentStep === 1 ? {
                                    background: generateGradientBackground(formData.theme_background_color),
                                    transition: 'background 0.3s ease'
                                } : {}}
                            >
                                {currentStep === 0 ? (
                                    /* Basic Information Form */
                                    <div className="max-w-2xl pt-16 pb-8 px-8 space-y-8">
                                        <div>
                                            <h2 className="text-2xl font-light text-gray-900 mb-8">
                                                {t('Questionnaires.Builder.BasicInfoHeading')}
                                            </h2>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                                {t('Questionnaires.Builder.LabelQuestionnaireTitle')}
                                            </label>
                                            <TextInput
                                                value={formData.title}
                                                onChange={(e) => updateFormData({ title: e.target.value })}
                                                placeholder={t('Questionnaires.Builder.PlaceholderTitle')}
                                                error={errors.title}
                                                className="text-lg"
                                            />
                                            {errors.title && (
                                                <Text className="text-red-600 text-sm mt-1">{errors.title}</Text>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                                {t('Questionnaires.Builder.LabelDescription')}
                                            </label>
                                            <Textarea
                                                value={formData.description}
                                                onChange={(e) => updateFormData({ description: e.target.value })}
                                                placeholder={t('Questionnaires.Builder.PlaceholderDescription')}
                                                rows={4}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                                {t('Questionnaires.Builder.LabelRecordingOptions')}
                                            </label>
                                            <div className="space-y-3">
                                                <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.record_camera}
                                                        onChange={(e) => updateFormData({ record_camera: e.target.checked })}
                                                        className="w-4 h-4 rounded accent-blue-600"
                                                    />
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-800">{t('Questionnaires.Builder.LabelRecordCamera')}</div>
                                                        <div className="text-xs text-gray-500">{t('Questionnaires.Builder.DescRecordCamera')}</div>
                                                    </div>
                                                </label>
                                                <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.record_screen}
                                                        onChange={(e) => updateFormData({ record_screen: e.target.checked })}
                                                        className="w-4 h-4 rounded accent-blue-600"
                                                    />
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-800">{t('Questionnaires.Builder.LabelRecordScreen')}</div>
                                                        <div className="text-xs text-gray-500">{t('Questionnaires.Builder.DescRecordScreen')}</div>
                                                    </div>
                                                </label>
                                                <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.record_mouse}
                                                        onChange={(e) => updateFormData({ record_mouse: e.target.checked })}
                                                        className="w-4 h-4 rounded accent-blue-600"
                                                    />
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-800">{t('Questionnaires.Builder.LabelRecordMouse')}</div>
                                                        <div className="text-xs text-gray-500">{t('Questionnaires.Builder.DescRecordMouse')}</div>
                                                    </div>
                                                </label>
                                                {/* Eye tracking requires both camera + screen recording (desktop only). */}
                                                <label className={`flex items-center gap-3 p-3 rounded-xl border border-gray-200 transition-colors ${(formData.record_camera && formData.record_screen) ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60 cursor-not-allowed'}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.record_gaze && formData.record_camera && formData.record_screen}
                                                        disabled={!(formData.record_camera && formData.record_screen)}
                                                        onChange={(e) => updateFormData({ record_gaze: e.target.checked })}
                                                        className="w-4 h-4 rounded accent-blue-600"
                                                    />
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-800">{t('Questionnaires.Builder.LabelRecordGaze')}</div>
                                                        <div className="text-xs text-gray-500">{t('Questionnaires.Builder.DescRecordGaze')}</div>
                                                        {!(formData.record_camera && formData.record_screen) && (
                                                            <div className="text-xs text-amber-600 mt-1">{t('Questionnaires.Builder.GazeRequires')}</div>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        {formData.is_timed && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                                        <ClockIcon className="inline h-4 w-4 mr-1" />
                                                        {t('Questionnaires.Builder.LabelViewDuration')}
                                                    </label>
                                                    <TextInput
                                                        type="number"
                                                        min="1"
                                                        max="300"
                                                        value={formData.default_view_duration}
                                                        onChange={(e) => updateFormData({ default_view_duration: parseInt(e.target.value, 10) })}
                                                        error={errors.default_view_duration}
                                                    />
                                                    {errors.default_view_duration && (
                                                        <Text className="text-red-600 text-sm mt-1">{errors.default_view_duration}</Text>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                                        <ClockIcon className="inline h-4 w-4 mr-1" />
                                                        {t('Questionnaires.Builder.LabelAnswerDuration')}
                                                    </label>
                                                    <TextInput
                                                        type="number"
                                                        min="5"
                                                        max="600"
                                                        value={formData.default_answer_duration}
                                                        onChange={(e) => updateFormData({ default_answer_duration: parseInt(e.target.value, 10) })}
                                                        error={errors.default_answer_duration}
                                                    />
                                                    {errors.default_answer_duration && (
                                                        <Text className="text-red-600 text-sm mt-1">{errors.default_answer_duration}</Text>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* Question Editing */
                                    <div className="max-w-4xl mx-auto pt-16 pb-8 px-8">
                                        {currentQuestion && (
                                            <>
                                                <div className="space-y-8">
                                                    {(currentQuestion.type === QUESTION_TYPES.MULTI_SECTION || true) ? (
                                                        /* Multi-Section Page Layout */
                                                        <div className="space-y-8">
                                                            {/* Multi-Section Page Title */}
                                                            <div className="text-center">
                                                                <input
                                                                    type="text"
                                                                    value={currentQuestion.question_text}
                                                                    onChange={(e) => updateQuestion(currentQuestion.id, { question_text: e.target.value })}
                                                                    placeholder={t('Questionnaires.Builder.PlaceholderMultiSectionTitle')}
                                                                    className="w-full text-2xl md:text-3xl font-light bg-transparent border-0 focus:outline-none focus:ring-0 questionnaire-input leading-tight text-center p-0 m-0"
                                                                    style={getTextColorStyle()}
                                                                    maxLength={200}
                                                                />
                                                            </div>

                                                            {/* Multi-Section Content Preview */}
                                                            <div className="space-y-6 border-2 border-dashed border-gray-300 rounded-lg p-6">
                                                                {currentQuestion.sections && currentQuestion.sections.length > 0 ? (
                                                                    currentQuestion.sections.map((section, sectionIndex) => (
                                                                        <div 
                                                                            key={section.id || sectionIndex}
                                                                            className="py-8"
                                                                        >
                                                                            <div className="space-y-6 text-left">
                                                                                {/* Editable Question Text - Same as individual pages */}
                                                                                <div>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={section.question_text}
                                                                                        onChange={(e) => updateSection(currentQuestion.id, section.id, { question_text: e.target.value })}
                                                                                        placeholder={t('Questionnaires.Builder.PlaceholderQuestion')}
                                                                                        className="w-full text-2xl md:text-3xl font-light bg-transparent border-0 focus:outline-none focus:ring-0 questionnaire-input leading-tight text-left p-0 m-0"
                                                                                        style={getTextColorStyle()}
                                                                                        maxLength={200}
                                                                                    />
                                                                                </div>
                                                                                
                                                                                {/* Optional Description Input - Same as individual pages */}
                                                                                <div>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={section.description || ''}
                                                                                        onChange={(e) => updateSection(currentQuestion.id, section.id, { description: e.target.value })}
                                                                                        placeholder={t('Questionnaires.Builder.PlaceholderDescriptionOptional')}
                                                                                        className="w-full text-base italic bg-transparent border-0 focus:outline-none focus:ring-0 questionnaire-input text-left p-0 m-0"
                                                                                        style={getTextColorStyle()}
                                                                                        maxLength={500}
                                                                                    />
                                                                                </div>
                                                                                
                                                                                {/* Media Area - Exact same logic as QuestionnaireViewer.js */}
                                                                                {(section.media_url || section.image_url || section.video_url) && 
                                                                                 (section.type === 'image_then_question' || section.type === 'image_choice' || section.type === 'video_only') && (
                                                                                    <div className="rounded-lg p-4 text-center">
                                                                                        {/* Check if it's a video first */}
                                                                                        {(section.media_url || section.video_url) && 
                                                                                         (section.media_url?.includes('.mp4') || section.media_url?.includes('.webm') || 
                                                                                          section.media_url?.includes('.mov') || section.video_url || section.type === 'video_only') ? (
                                                                                            <video 
                                                                                                controls
                                                                                                className="w-full max-h-[50vh] mx-auto rounded-lg shadow-lg"
                                                                                                onError={(e) => {
                                                                                                    console.error('Failed to load video:', section.media_url || section.video_url)
                                                                                                }}
                                                                                            >
                                                                                                <source src={section.media_url || section.video_url} type="video/mp4" />
                                                                                                Your browser does not support the video tag.
                                                                                            </video>
                                                                                        ) : (
                                                                                            <img 
                                                                                                src={section.media_url || section.image_url}
                                                                                                alt="Question visual"
                                                                                                className="w-full max-h-[50vh] object-contain mx-auto rounded-lg shadow-lg"
                                                                                                onError={(e) => {
                                                                                                    console.error('Failed to load image:', section.media_url || section.image_url)
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                                
                                                                                {/* Answer Component - Exact same logic as QuestionnaireViewer.js */}
                                                                                <div>
                                                                                    {(() => {
                                                                                        switch (section.type) {
                                                                                            case 'text':
                                                                                                return (
                                                                                                    <>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value=""
                                                                                                            placeholder={t('Questionnaires.Builder.PlaceholderAnswerHere')}
                                                                                                            readOnly
                                                                                                            autoComplete="off"
                                                                                                            autoCorrect="off"
                                                                                                            autoCapitalize="off"
                                                                                                            spellCheck="false"
                                                                                                            className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input cursor-default"
                                                                                                            style={{ 
                                                                                                                color: getTextColorStyle().color,
                                                                                                                borderBottomColor: formData.theme_button_color || '#1e40af'
                                                                                                            }}
                                                                                                        />
                                                                                                        
                                                                                                        <p className="text-sm mt-2 text-left p-0 m-0" 
                                                                                                           style={getTextColorStyle(true)}
                                                                                                        >
                                                                                                            {t('Questionnaires.Builder.HelperLineBreak')}
                                                                                                        </p>
                                                                                                    </>
                                                                                                );

                                                                                            case 'text_only':
                                                                                                return (
                                                                                                    <div className="text-center text-gray-500 italic py-8">
                                                                                                        {/* This is a text-only page - no input required */}
                                                                                                    </div>
                                                                                                );

                                                                                            case 'textarea':
                                                                                                return (
                                                                                                    <>
                                                                                                        <textarea
                                                                                                            value=""
                                                                                                            rows={4}
                                                                                                            placeholder={t('Questionnaires.Builder.PlaceholderAnswerHere')}
                                                                                                            readOnly
                                                                                                            autoComplete="off"
                                                                                                            autoCorrect="off"
                                                                                                            autoCapitalize="off"
                                                                                                            spellCheck="false"
                                                                                                            className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 resize-none questionnaire-input cursor-default"
                                                                                                            style={{ 
                                                                                                                color: getTextColorStyle().color,
                                                                                                                borderBottomColor: formData.theme_button_color || '#1e40af'
                                                                                                            }}
                                                                                                        />
                                                                                                        
                                                                                                        <p className="text-sm mt-2 text-left p-0 m-0" 
                                                                                                           style={getTextColorStyle(true)}
                                                                                                        >
                                                                                                            {t('Questionnaires.Builder.HelperLineBreak')}
                                                                                                        </p>
                                                                                                    </>
                                                                                                );

                                                                                            case 'voice_recording':
                                                                                                return (
                                                                                                    <div className="flex justify-center">
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            disabled
                                                                                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium opacity-80"
                                                                                                            style={{ backgroundColor: formData.theme_button_color || '#1e40af' }}
                                                                                                        >
                                                                                                            <MicrophoneIcon className="w-5 h-5" />
                                                                                                            {t('Questionnaires.Builder.TypeVoiceRecording')}
                                                                                                        </button>
                                                                                                    </div>
                                                                                                );

                                                                                            case 'yes_no':
                                                                                                return (
                                                                                                    <div className="space-y-3">
                                                                                                        <div className="flex items-center space-x-3 justify-center">
                                                                                                            <input
                                                                                                                type="radio"
                                                                                                                name={`preview-${section.id}`}
                                                                                                                value="yes"
                                                                                                                disabled
                                                                                                                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                                                                                                                style={{ 
                                                                                                                    accentColor: formData.theme_button_color || '#1e40af',
                                                                                                                    outline: 'none',
                                                                                                                    border: 'none'
                                                                                                                }}
                                                                                                            />
                                                                                                            <label className="text-lg cursor-default" style={getTextColorStyle()}>
                                                                                                                Yes
                                                                                                            </label>
                                                                                                        </div>
                                                                                                        <div className="flex items-center space-x-3 justify-center">
                                                                                                            <input
                                                                                                                type="radio"
                                                                                                                name={`preview-${section.id}`}
                                                                                                                value="no"
                                                                                                                disabled
                                                                                                                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                                                                                                                style={{ 
                                                                                                                    accentColor: formData.theme_button_color || '#1e40af',
                                                                                                                    outline: 'none',
                                                                                                                    border: 'none'
                                                                                                                }}
                                                                                                            />
                                                                                                            <label className="text-lg cursor-default" style={getTextColorStyle()}>
                                                                                                                No
                                                                                                            </label>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );

                                                                                            case 'multiple_choice':
                                                                                                return (
                                                                                                    <div className="space-y-4">
                                                                                                        {/* Options Management - Same as individual pages */}
                                                                                                        <div className="flex items-center justify-between">
                                                                                                            <label className="block text-sm font-medium text-gray-700">
                                                                                                                Options
                                                                                                            </label>
                                                                                                            <Button
                                                                                                                size="xs"
                                                                                                                icon={PlusIcon}
                                                                                                                onClick={() => addSectionOption(currentQuestion.id, section.id)}
                                                                                                                className="bg-iavisus-100 text-white"
                                                                                                            >
                                                                                                                {t('Questionnaires.Builder.BtnAddOption')}
                                                                                                            </Button>
                                                                                                        </div>

                                                                                                        {(!section.options || section.options.length === 0) ? (
                                                                                                            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                                                                                                <Text className="text-gray-500 mb-4">
                                                                                                                    {t('Questionnaires.Builder.NoOptionsYet')}
                                                                                                                </Text>
                                                                                                                <Button
                                                                                                                    size="sm"
                                                                                                                    icon={PlusIcon}
                                                                                                                    onClick={() => addSectionOption(currentQuestion.id, section.id)}
                                                                                                                    className="bg-iavisus-100 text-white"
                                                                                                                >
                                                                                                                    {t('Questionnaires.Builder.BtnAddFirstOption')}
                                                                                                                </Button>
                                                                                                            </div>
                                                                                                        ) : (
                                                                                                            <div className="space-y-3">
                                                                                                                {(section.options || []).map((option, optIndex) => (
                                                                                                                    <div key={option.id || optIndex} className="p-3 bg-gray-50 rounded-lg space-y-3">
                                                                                                                        {/* Option Number and Text */}
                                                                                                                        <div className="flex items-center space-x-3">
                                                                                                                            <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs">
                                                                                                                                {optIndex + 1}
                                                                                                                            </div>
                                                                                                                            <TextInput
                                                                                                                                placeholder={t('Questionnaires.Builder.PlaceholderOptionText')}
                                                                                                                                value={option.text || ''}
                                                                                                                                onChange={(e) => updateSectionOption(currentQuestion.id, section.id, optIndex, { text: e.target.value })}
                                                                                                                                className="flex-1"
                                                                                                                            />
                                                                                                                            <button
                                                                                                                                onClick={() => deleteSectionOption(currentQuestion.id, section.id, optIndex)}
                                                                                                                                className="text-red-500 hover:text-red-700"
                                                                                                                            >
                                                                                                                                <TrashIcon className="h-4 w-4" />
                                                                                                                            </button>
                                                                                                                        </div>

                                                                                                                        {/* Image Upload Area for Multiple Choice Options */}
                                                                                                                        <div className="ml-9">
                                                                                                                            {option.image_url ? (
                                                                                                                                /* Show uploaded image */
                                                                                                                                <div className="flex items-center space-x-3">
                                                                                                                                    <img 
                                                                                                                                        src={option.image_url}
                                                                                                                                        alt={`Option ${optIndex + 1} image`}
                                                                                                                                        className="w-16 h-16 object-cover rounded-lg border-2 border-gray-200"
                                                                                                                                        onError={(e) => {
                                                                                                                                            console.error(`Failed to load option image:`, option.image_url)
                                                                                                                                            e.target.style.display = 'none'
                                                                                                                                        }}
                                                                                                                                    />
                                                                                                                                    <div className="flex flex-col space-y-2">
                                                                                                                                        <Button
                                                                                                                                            size="xs"
                                                                                                                                            icon={PhotoIcon}
                                                                                                                                            onClick={() => {
                                                                                                                                                const input = document.createElement('input')
                                                                                                                                                input.type = 'file'
                                                                                                                                                input.accept = 'image/*'
                                                                                                                                                input.onchange = (e) => handleSectionOptionImageUpload(currentQuestion.id, section.id, optIndex, e.target.files[0])
                                                                                                                                                input.click()
                                                                                                                                            }}
                                                                                                                                            className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                                                                                                        >
                                                                                                                                            {t('Questionnaires.Builder.BtnReplaceImage')}
                                                                                                                                        </Button>
                                                                                                                                        <Button
                                                                                                                                            size="xs"
                                                                                                                                            icon={TrashIcon}
                                                                                                                                            onClick={() => updateSectionOption(currentQuestion.id, section.id, optIndex, { image_url: null })}
                                                                                                                                            className="bg-red-100 text-red-700 hover:bg-red-200"
                                                                                                                                        >
                                                                                                                                            {t('Questionnaires.Builder.BtnRemoveImage')}
                                                                                                                                        </Button>
                                                                                                                                    </div>
                                                                                                                                </div>
                                                                                                                            ) : (
                                                                                                                                /* Image upload area */
                                                                                                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                                                                                                                    <PhotoIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                                                                                                                                    <Text className="text-sm text-gray-500 mb-2">
                                                                                                                                        {t('Questionnaires.Builder.BtnUploadImage')}
                                                                                                                                    </Text>
                                                                                                                                    <Button
                                                                                                                                        size="xs"
                                                                                                                                        icon={PhotoIcon}
                                                                                                                                        onClick={() => {
                                                                                                                                            const input = document.createElement('input')
                                                                                                                                            input.type = 'file'
                                                                                                                                            input.accept = 'image/*'
                                                                                                                                            input.onchange = (e) => handleSectionOptionImageUpload(currentQuestion.id, section.id, optIndex, e.target.files[0])
                                                                                                                                            input.click()
                                                                                                                                        }}
                                                                                                                                        className="bg-iavisus-100 text-white"
                                                                                                                                    >
                                                                                                                                        {t('Questionnaires.Builder.BtnChooseImage')}
                                                                                                                                    </Button>
                                                                                                                                </div>
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                ))}
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                );

                                                                                            case 'rating':
                                                                                                return (
                                                                                                    <div className="space-y-4">
                                                                                                        {/* Rating Options Management - Same as individual pages */}
                                                                                                        <div className="flex items-center justify-between">
                                                                                                            <label className="block text-sm font-medium text-gray-700">
                                                                                                                {t('Questionnaires.Builder.LabelRatingOptions')}
                                                                                                            </label>
                                                                                                            <Button
                                                                                                                size="xs"
                                                                                                                icon={PlusIcon}
                                                                                                                onClick={() => addSectionOption(currentQuestion.id, section.id)}
                                                                                                                className="bg-iavisus-100 text-white"
                                                                                                            >
                                                                                                                {t('Questionnaires.Builder.BtnAddOption')}
                                                                                                            </Button>
                                                                                                        </div>

                                                                                                        {(!section.options || section.options.length === 0) ? (
                                                                                                            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                                                                                                <Text className="text-gray-500 mb-4">
                                                                                                                    {t('Questionnaires.Builder.NoRatingOptionsYet')}
                                                                                                                </Text>
                                                                                                                <Button
                                                                                                                    size="sm"
                                                                                                                    icon={PlusIcon}
                                                                                                                    onClick={() => addSectionOption(currentQuestion.id, section.id)}
                                                                                                                    className="bg-iavisus-100 text-white"
                                                                                                                >
                                                                                                                    {t('Questionnaires.Builder.BtnAddFirstOption')}
                                                                                                                </Button>
                                                                                                            </div>
                                                                                                        ) : (
                                                                                                            <div className="space-y-3">
                                                                                                                {(section.options || []).map((option, optIndex) => (
                                                                                                                    <div key={option.id || optIndex} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                                                                                                        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs">
                                                                                                                            {optIndex + 1}
                                                                                                                        </div>
                                                                                                                        <TextInput
                                                                                                                            placeholder={t('Questionnaires.Builder.PlaceholderRatingLabel')}
                                                                                                                            value={option.text || ''}
                                                                                                                            onChange={(e) => updateSectionOption(currentQuestion.id, section.id, optIndex, { text: e.target.value })}
                                                                                                                            className="flex-1"
                                                                                                                        />
                                                                                                                        <button
                                                                                                                            onClick={() => deleteSectionOption(currentQuestion.id, section.id, optIndex)}
                                                                                                                            className="text-red-500 hover:text-red-700"
                                                                                                                        >
                                                                                                                            <TrashIcon className="h-4 w-4" />
                                                                                                                        </button>
                                                                                                                    </div>
                                                                                                                ))}
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                );

                                                                                            case 'table':
                                                                                                return (
                                                                                                    <div className="space-y-4">
                                                                                                        {/* Table Configuration */}
                                                                                                        <div className="space-y-4">
                                                                                                            <div className="flex items-center space-x-4">
                                                                                                                <Button
                                                                                                                    size="xs"
                                                                                                                    icon={PlusIcon}
                                                                                                                    onClick={() => addTableRow(currentQuestion.id, section.id)}
                                                                                                                    className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                                                                >
                                                                                                                    {t('Questionnaires.Builder.BtnAddRow')}
                                                                                                                </Button>
                                                                                                                <Button
                                                                                                                    size="xs"
                                                                                                                    icon={PlusIcon}
                                                                                                                    onClick={() => addTableColumn(currentQuestion.id, section.id)}
                                                                                                                    className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                                                                >
                                                                                                                    {t('Questionnaires.Builder.BtnAddColumn')}
                                                                                                                </Button>
                                                                                                            </div>

                                                                                                            {/* Table Preview */}
                                                                                                            {section.table_data && (
                                                                                                                <div className="border border-gray-300 rounded-lg overflow-hidden">
                                                                                                                    <div className="bg-gray-50 p-3 border-b border-gray-300">
                                                                                                                        <Text className="text-sm font-medium text-gray-700">{t('Questionnaires.Builder.HeaderTablePreview')}</Text>
                                                                                                                    </div>
                                                                                                                    <div className="p-4 overflow-x-auto">
                                                                                                                        <table className="w-full border-collapse min-w-max">
                                                                                                                            {/* Show header row only when header_mode is 'headers' */}
                                                                                                                            {(section.table_data.header_mode !== 'cells') && (
                                                                                                                                <thead>
                                                                                                                                    <tr>
                                                                                                                                        <th className="border border-gray-300 p-2 bg-gray-50"></th>
                                                                                                                                        {(section.table_data.columns || []).map((column, colIndex) => (
                                                                                                                                            <th key={column.id} className="border border-gray-300 p-2 bg-gray-50">
                                                                                                                                                <div className="flex items-center justify-between space-x-2">
                                                                                                                                                    <TextInput
                                                                                                                                                        placeholder={t('Questionnaires.Builder.PlaceholderColumnHeader')}
                                                                                                                                                        value={column.text || ''}
                                                                                                                                                        onChange={(e) => updateTableColumn(currentQuestion.id, section.id, colIndex, { text: e.target.value })}
                                                                                                                                                        className="flex-1 text-sm"
                                                                                                                                                    />
                                                                                                                                                    <button
                                                                                                                                                        onClick={() => deleteTableColumn(currentQuestion.id, section.id, colIndex)}
                                                                                                                                                        className="text-red-500 hover:text-red-700 p-1"
                                                                                                                                                    >
                                                                                                                                                        <TrashIcon className="h-3 w-3" />
                                                                                                                                                    </button>
                                                                                                                                                </div>
                                                                                                                                            </th>
                                                                                                                                        ))}
                                                                                                                                    </tr>
                                                                                                                                </thead>
                                                                                                                            )}
                                                                                                                            {/* Show header management row when header_mode is 'cells' */}
                                                                                                                            {(section.table_data.header_mode === 'cells') && (
                                                                                                                                <thead>
                                                                                                                                    <tr>
                                                                                                                                        <th className="border border-gray-300 p-2 bg-gray-50">
                                                                                                                                            <Text className="text-xs text-gray-600">{t('Questionnaires.Builder.LabelColumnHeaders')}</Text>
                                                                                                                                        </th>
                                                                                                                                        {(section.table_data.columns || []).map((column, colIndex) => (
                                                                                                                                            <th key={column.id} className="border border-gray-300 p-2 bg-gray-50">
                                                                                                                                                <div className="flex items-center justify-between space-x-2">
                                                                                                                                                    <TextInput
                                                                                                                                                        placeholder={t('Questionnaires.Builder.PlaceholderColumnHeader')}
                                                                                                                                                        value={column.text || ''}
                                                                                                                                                        onChange={(e) => updateTableColumn(currentQuestion.id, section.id, colIndex, { text: e.target.value })}
                                                                                                                                                        className="flex-1 text-sm"
                                                                                                                                                    />
                                                                                                                                                    <button
                                                                                                                                                        onClick={() => deleteTableColumn(currentQuestion.id, section.id, colIndex)}
                                                                                                                                                        className="text-red-500 hover:text-red-700 p-1"
                                                                                                                                                    >
                                                                                                                                                        <TrashIcon className="h-3 w-3" />
                                                                                                                                                    </button>
                                                                                                                                                </div>
                                                                                                                                            </th>
                                                                                                                                        ))}
                                                                                                                                    </tr>
                                                                                                                                </thead>
                                                                                                                            )}
                                                                                                                            <tbody>
                                                                                                                                {(section.table_data.rows || []).map((row, rowIndex) => (
                                                                                                                                    <tr key={row.id}>
                                                                                                                                        <td className="border border-gray-300 p-2 bg-gray-50">
                                                                                                                                            <div className="flex items-center justify-between space-x-2">
                                                                                                                                                <TextInput
                                                                                                                                                    placeholder={t('Questionnaires.Builder.PlaceholderRowLabel')}
                                                                                                                                                    value={row.text || ''}
                                                                                                                                                    onChange={(e) => updateTableRow(currentQuestion.id, section.id, rowIndex, { text: e.target.value })}
                                                                                                                                                    className="flex-1 text-sm"
                                                                                                                                                />
                                                                                                                                                <button
                                                                                                                                                    onClick={() => deleteTableRow(currentQuestion.id, section.id, rowIndex)}
                                                                                                                                                    className="text-red-500 hover:text-red-700 p-1"
                                                                                                                                                >
                                                                                                                                                    <TrashIcon className="h-3 w-3" />
                                                                                                                                                </button>
                                                                                                                                            </div>
                                                                                                                                        </td>
                                                                                                                                        {(section.table_data.columns || []).map((column, colIndex) => (
                                                                                                                                            <td key={`${row.id}-${column.id}`} className="border border-gray-300 p-2">
                                                                                                                                                <div className="flex items-center space-x-2">
                                                                                                                                                    <input
                                                                                                                                                        type="radio"
                                                                                                                                                        name={`table-${section.id}-row-${rowIndex}`}
                                                                                                                                                        className="h-4 w-4 flex-shrink-0"
                                                                                                                                                        disabled
                                                                                                                                                    />
                                                                                                                                                    {/* Show column header text in cell when header_mode is 'cells' */}
                                                                                                                                                    {(section.table_data.header_mode === 'cells') && (
                                                                                                                                                        <span className="text-xs text-gray-600">{column.text || `Column ${colIndex + 1}`}</span>
                                                                                                                                                    )}
                                                                                                                                                </div>
                                                                                                                                            </td>
                                                                                                                                        ))}
                                                                                                                                    </tr>
                                                                                                                                ))}
                                                                                                                            </tbody>
                                                                                                                        </table>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            )}

                                                                                                            {!section.table_data && (
                                                                                                                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                                                                                                    <Text className="text-gray-500 mb-4">
                                                                                                                        {t('Questionnaires.Builder.NoTableYet')}
                                                                                                                    </Text>
                                                                                                                    <Button
                                                                                                                        size="sm"
                                                                                                                        icon={PlusIcon}
                                                                                                                        onClick={() => {
                                                                                                                            const tableData = initializeDefaultTableOptions()
                                                                                                                            updateSection(currentQuestion.id, section.id, { table_data: tableData })
                                                                                                                        }}
                                                                                                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                                                                    >
                                                                                                                        {t('Questionnaires.Builder.BtnCreateTable')}
                                                                                                                    </Button>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );

                                                                                            case 'image_only':
                                                                                            case 'video_only':
                                                                                                return (
                                                                                                    <div className="space-y-4">
                                                                                                        {/* Media Upload Area - Same as individual pages */}
                                                                                                        <div className="text-center">
                                                                                                            <div 
                                                                                                                className={`relative border-2 border-dashed rounded-lg p-8 transition-colors ${
                                                                                                                    isDragOver ? 'border-iavisus-100 bg-blue-50' : 'border-gray-300'
                                                                                                                }`}
                                                                                                                onDrop={(e) => {
                                                                                                                    e.preventDefault()
                                                                                                                    setIsDragOver(false)
                                                                                                                    const files = Array.from(e.dataTransfer.files)
                                                                                                                    if (files.length > 0) handleSectionMediaUpload(currentQuestion.id, section.id, files[0])
                                                                                                                }}
                                                                                                                onDragOver={(e) => e.preventDefault()}
                                                                                                                onDragEnter={(e) => {
                                                                                                                    e.preventDefault()
                                                                                                                    setIsDragOver(true)
                                                                                                                }}
                                                                                                                onDragLeave={(e) => {
                                                                                                                    e.preventDefault()
                                                                                                                    setIsDragOver(false)
                                                                                                                }}
                                                                                                            >
                                                                                                                {section.media_url ? (
                                                                                                                    /* Show uploaded media */
                                                                                                                    <div className="space-y-4">
                                                                                                                        {section.type === 'image_only' ? (
                                                                                                                            <img 
                                                                                                                                src={section.media_url}
                                                                                                                                alt="Section image"
                                                                                                                                className="max-h-64 mx-auto rounded-lg shadow-md"
                                                                                                                                onError={(e) => {
                                                                                                                                    console.error('Failed to load section image:', section.media_url)
                                                                                                                                    e.target.style.display = 'none'
                                                                                                                                }}
                                                                                                                            />
                                                                                                                        ) : (
                                                                                                                            <video 
                                                                                                                                controls
                                                                                                                                className="max-h-64 mx-auto rounded-lg shadow-md"
                                                                                                                            >
                                                                                                                                <source src={section.media_url} type="video/mp4" />
                                                                                                                                Your browser does not support the video tag.
                                                                                                                            </video>
                                                                                                                        )}
                                                                                                                        
                                                                                                                        <div className="flex justify-center space-x-3">
                                                                                                                            <Button
                                                                                                                                size="xs"
                                                                                                                                icon={PhotoIcon}
                                                                                                                                onClick={() => {
                                                                                                                                    const input = document.createElement('input')
                                                                                                                                    input.type = 'file'
                                                                                                                                    input.accept = section.type === 'image_only' ? 'image/*' : 'video/*'
                                                                                                                                    input.onchange = (e) => handleSectionMediaUpload(currentQuestion.id, section.id, e.target.files[0])
                                                                                                                                    input.click()
                                                                                                                                }}
                                                                                                                                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                                                                                            >
                                                                                                                                {section.type === 'image_only' ? t('Questionnaires.Builder.BtnReplaceImage') : t('Questionnaires.Builder.BtnReplaceVideo')}
                                                                                                                            </Button>
                                                                                                                            <Button
                                                                                                                                size="xs"
                                                                                                                                icon={TrashIcon}
                                                                                                                                onClick={() => updateSection(currentQuestion.id, section.id, { media_url: null })}
                                                                                                                                className="bg-red-100 text-red-700 hover:bg-red-200"
                                                                                                                            >
                                                                                                                                {section.type === 'image_only' ? t('Questionnaires.Builder.BtnRemoveImage') : t('Questionnaires.Builder.BtnRemoveVideo')}
                                                                                                                            </Button>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                ) : uploadingMedia ? (
                                                                                                                    /* Show loading state */
                                                                                                                    <div className="space-y-4">
                                                                                                                        <div className="h-16 w-16 mx-auto animate-spin rounded-full border-4 border-gray-300 border-t-iavisus-100"></div>
                                                                                                                        <Text className="text-gray-500">
                                                                                                                            {t('Questionnaires.Builder.UploadingMedia')}
                                                                                                                        </Text>
                                                                                                                    </div>
                                                                                                                ) : (
                                                                                                                    /* Show upload prompt */
                                                                                                                    <div className="space-y-4">
                                                                                                                        <div className="text-gray-400">
                                                                                                                            {section.type === 'image_only' ? (
                                                                                                                                <PhotoIcon className="h-12 w-12 mx-auto" />
                                                                                                                            ) : (
                                                                                                                                <PlayIcon className="h-12 w-12 mx-auto" />
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                        <div className="space-y-2">
                                                                                                                            <Text className="text-lg font-medium">
                                                                                                                                {section.type === 'image_only' ? t('Questionnaires.Builder.AddImage') : t('Questionnaires.Builder.AddVideo')}
                                                                                                                            </Text>
                                                                                                                            <Text className="text-sm text-gray-500">
                                                                                                                                {t('Questionnaires.Builder.DragDropOrClick')}
                                                                                                                            </Text>
                                                                                                                        </div>
                                                                                                                        <Button
                                                                                                                            size="sm"
                                                                                                                            icon={PhotoIcon}
                                                                                                                            onClick={() => {
                                                                                                                                const input = document.createElement('input')
                                                                                                                                input.type = 'file'
                                                                                                                                input.accept = section.type === 'image_only' ? 'image/*' : 'video/*'
                                                                                                                                input.onchange = (e) => handleSectionMediaUpload(currentQuestion.id, section.id, e.target.files[0])
                                                                                                                                input.click()
                                                                                                                            }}
                                                                                                                            className="bg-iavisus-100 text-white"
                                                                                                                        >
                                                                                                                            {section.type === 'image_only' ? t('Questionnaires.Builder.BtnChooseImage') : t('Questionnaires.Builder.BtnChooseVideo')}
                                                                                                                        </Button>
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );

                                                                                            case 'audio_only':
                                                                                                return (
                                                                                                    <div className="space-y-4">
                                                                                                        <div
                                                                                                            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragOver ? 'border-iavisus-100 bg-blue-50' : 'border-gray-300'}`}
                                                                                                            onDrop={(e) => {
                                                                                                                e.preventDefault()
                                                                                                                setIsDragOver(false)
                                                                                                                const file = e.dataTransfer.files[0]
                                                                                                                if (file) handleSectionMediaUpload(currentQuestion.id, section.id, file)
                                                                                                            }}
                                                                                                            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
                                                                                                            onDragEnter={() => setIsDragOver(true)}
                                                                                                            onDragLeave={() => setIsDragOver(false)}
                                                                                                        >
                                                                                                            {section.media_url ? (
                                                                                                                <div className="space-y-4">
                                                                                                                    <audio controls className="w-full mx-auto" src={section.media_url}>
                                                                                                                        Your browser does not support the audio element.
                                                                                                                    </audio>
                                                                                                                    <div className="flex justify-center space-x-3">
                                                                                                                        <Button
                                                                                                                            size="sm"
                                                                                                                            onClick={() => {
                                                                                                                                const input = document.createElement('input')
                                                                                                                                input.type = 'file'
                                                                                                                                input.accept = 'audio/*'
                                                                                                                                input.onchange = (e) => handleSectionMediaUpload(currentQuestion.id, section.id, e.target.files[0])
                                                                                                                                input.click()
                                                                                                                            }}
                                                                                                                            className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                                                                                                                        >
                                                                                                                            {t('Questionnaires.Builder.BtnReplaceFile')}
                                                                                                                        </Button>
                                                                                                                        <Button
                                                                                                                            size="sm"
                                                                                                                            icon={MicrophoneIcon}
                                                                                                                            onClick={() => startSectionRecording(currentQuestion.id, section.id)}
                                                                                                                            className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                                                                                                                        >
                                                                                                                            {t('Questionnaires.Builder.BtnReRecord')}
                                                                                                                        </Button>
                                                                                                                        <Button
                                                                                                                            size="sm"
                                                                                                                            onClick={() => updateSection(currentQuestion.id, section.id, { media_url: null })}
                                                                                                                            className="bg-red-100 text-red-700 hover:bg-red-200"
                                                                                                                        >
                                                                                                                            {t('Questionnaires.Builder.BtnRemoveAudio')}
                                                                                                                        </Button>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ) : uploadingMedia ? (
                                                                                                                <div className="space-y-4">
                                                                                                                    <div className="h-16 w-16 mx-auto animate-spin rounded-full border-4 border-gray-300 border-t-iavisus-100"></div>
                                                                                                                    <Text className="text-gray-500">
                                                                                                                        {t('Questionnaires.Builder.UploadingMedia')}
                                                                                                                    </Text>
                                                                                                                </div>
                                                                                                            ) : recordingSection === section.id ? (
                                                                                                                <div className="space-y-4">
                                                                                                                    <div className="flex items-center justify-center space-x-3">
                                                                                                                        <span className="inline-block h-4 w-4 rounded-full bg-red-500 animate-pulse" />
                                                                                                                        <Text className="text-red-600 font-semibold text-lg">
                                                                                                                            {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:{String(recordingTime % 60).padStart(2, '0')}
                                                                                                                        </Text>
                                                                                                                    </div>
                                                                                                                    <Text className="text-gray-500 text-sm">{t('Questionnaires.Builder.RecordingInProgress')}</Text>
                                                                                                                    <Button
                                                                                                                        size="sm"
                                                                                                                        icon={StopIcon}
                                                                                                                        onClick={stopSectionRecording}
                                                                                                                        className="bg-red-600 text-white hover:bg-red-700"
                                                                                                                    >
                                                                                                                        {t('Questionnaires.Builder.BtnStopRecording')}
                                                                                                                    </Button>
                                                                                                                </div>
                                                                                                            ) : (
                                                                                                                <div className="space-y-4">
                                                                                                                    <div className="text-gray-400">
                                                                                                                        <SpeakerWaveIcon className="h-12 w-12 mx-auto" />
                                                                                                                    </div>
                                                                                                                    <div className="space-y-2">
                                                                                                                        <Text className="text-lg font-medium">
                                                                                                                            {t('Questionnaires.Builder.AddAudio')}
                                                                                                                        </Text>
                                                                                                                        <Text className="text-sm text-gray-500">
                                                                                                                            {t('Questionnaires.Builder.AudioUploadOrRecord')}
                                                                                                                        </Text>
                                                                                                                    </div>
                                                                                                                    <div className="flex justify-center space-x-3">
                                                                                                                        <Button
                                                                                                                            size="sm"
                                                                                                                            icon={SpeakerWaveIcon}
                                                                                                                            onClick={() => {
                                                                                                                                const input = document.createElement('input')
                                                                                                                                input.type = 'file'
                                                                                                                                input.accept = 'audio/*'
                                                                                                                                input.onchange = (e) => handleSectionMediaUpload(currentQuestion.id, section.id, e.target.files[0])
                                                                                                                                input.click()
                                                                                                                            }}
                                                                                                                            className="bg-iavisus-100 text-white"
                                                                                                                        >
                                                                                                                            {t('Questionnaires.Builder.BtnChooseFile')}
                                                                                                                        </Button>
                                                                                                                        <Button
                                                                                                                            size="sm"
                                                                                                                            icon={MicrophoneIcon}
                                                                                                                            onClick={() => startSectionRecording(currentQuestion.id, section.id)}
                                                                                                                            className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                                                                                                        >
                                                                                                                            {t('Questionnaires.Builder.BtnRecordAudio')}
                                                                                                                        </Button>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );

                                                                                            default:
                                                                                                return (
                                                                                                    <>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value=""
                                                                                                            placeholder={t('Questionnaires.Builder.PlaceholderAnswerHere')}
                                                                                                            readOnly
                                                                                                            autoComplete="off"
                                                                                                            autoCorrect="off"
                                                                                                            autoCapitalize="off"
                                                                                                            spellCheck="false"
                                                                                                            className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input cursor-default"
                                                                                                            style={{ 
                                                                                                                color: getTextColorStyle().color,
                                                                                                                borderBottomColor: formData.theme_button_color || '#1e40af'
                                                                                                            }}
                                                                                                        />
                                                                                                        
                                                                                                        <p className="text-sm mt-2 text-left p-0 m-0" 
                                                                                                           style={getTextColorStyle(true)}
                                                                                                        >
                                                                                                            {t('Questionnaires.Builder.HelperLineBreak')}
                                                                                                        </p>
                                                                                                    </>
                                                                                                );
                                                                                        }
                                                                                    })()}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="text-center py-12">
                                                                        <div className="text-gray-400 mb-4">
                                                                            <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                                            </svg>
                                                                        </div>
                                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('Questionnaires.Builder.SectionNoSectionsYet')}</h3>
                                                                        <p className="text-gray-500 mb-4">
                                                                            {t('Questionnaires.Builder.SectionNoSectionsHint')}
                                                                        </p>
                                                                        <button
                                                                            onClick={() => addSection(currentQuestion.id, QUESTION_TYPES.TEXT)}
                                                                            className="inline-flex items-center px-4 py-2 bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200"
                                                                        >
                                                                            {t('Questionnaires.Builder.BtnAddFirstSection')}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : false && (currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY || currentQuestion.type === QUESTION_TYPES.VIDEO_ONLY || currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION) ? (
                                                        /* Media-Only Page Layout */
                                                        <div className="space-y-6 text-center">
                                                            {/* Page Title/Caption */}
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    value={currentQuestion.question_text}
                                                                    onChange={(e) => updateQuestion(currentQuestion.id, { question_text: e.target.value })}
                                                                    placeholder={
                                                                        currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY ? "Image caption or title..." :
                                                                        currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION ? "Question text..." :
                                                                        "Video title..."
                                                                    }
                                                                    className="w-full text-2xl md:text-3xl font-light bg-transparent border-0 focus:outline-none focus:ring-0 questionnaire-input leading-tight text-center p-0 m-0"
                                                                    style={getTextColorStyle()}
                                                                    maxLength={200}
                                                                />
                                                            </div>
                                                            
                                                            {/* Optional Description */}
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    value={currentQuestion.description || ''}
                                                                    onChange={(e) => updateQuestion(currentQuestion.id, { description: e.target.value })}
                                                                    placeholder={t('Questionnaires.Builder.PlaceholderDescriptionInstructions')}
                                                                    className="w-full text-base italic bg-transparent border-0 focus:outline-none focus:ring-0 questionnaire-input text-center p-0 m-0"
                                                                    style={getTextColorStyle()}
                                                                    maxLength={500}
                                                                />
                                                            </div>
                                                            
                                                            {/* Media Upload Area */}
                                                            <div 
                                                                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                                                                    isDragOver 
                                                                        ? 'border-iavisus-100 bg-blue-50' 
                                                                        : 'border-gray-300 hover:border-gray-400'
                                                                }`}
                                                                onDrop={handleFileDrop}
                                                                onDragOver={handleDragOver}
                                                                onDragEnter={handleDragEnter}
                                                                onDragLeave={handleDragLeave}
                                                            >
                                                                {currentQuestion.media_url ? (
                                                                    /* Show uploaded media */
                                                                    <div className="space-y-4">
                                                                        {(currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY || currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION) ? (
                                                                            <img 
                                                                                src={currentQuestion.media_url}
                                                                                alt="Uploaded image"
                                                                                className="max-h-64 mx-auto rounded-lg shadow-md"
                                                                                onError={(e) => {
                                                                                    console.error('Failed to load image:', currentQuestion.media_url)
                                                                                    e.target.style.display = 'none'
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <video 
                                                                                src={currentQuestion.media_url}
                                                                                controls
                                                                                className="max-h-64 mx-auto rounded-lg shadow-md"
                                                                                onError={(e) => {
                                                                                    console.error('Failed to load video:', currentQuestion.media_url)
                                                                                }}
                                                                            />
                                                                        )}
                                                                        
                                                                        <div>
                                                                            <Text className="text-gray-700 font-medium mb-2">
                                                                                {currentQuestion.media_filename}
                                                                            </Text>
                                                                            <div className="flex justify-center space-x-2">
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="secondary"
                                                                                    onClick={() => fileInputRef.current?.click()}
                                                                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                                                >
                                                                                    Replace
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="secondary"
                                                                                    onClick={() => updateQuestion(currentQuestion.id, { 
                                                                                        media_url: null, 
                                                                                        media_filename: null, 
                                                                                        media_type: null 
                                                                                    })}
                                                                                    className="bg-red-50 text-red-600 hover:bg-red-100"
                                                                                >
                                                                                    Remove
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : useChunkedUpload && chunkedUploadFile ? (
                                                                    /* Show chunked uploader */
                                                                    <QuestionnaireChunkedUploader
                                                                        ref={chunkedUploaderRef}
                                                                        file={chunkedUploadFile}
                                                                        fileName={chunkedUploadFile.name}
                                                                        mediaType={(
                                                                            currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY ||
                                                                            currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION
                                                                        ) ? 'image' : 'video'}
                                                                        onProgress={handleChunkedUploadProgress}
                                                                        onComplete={handleChunkedUploadComplete}
                                                                        onError={handleChunkedUploadError}
                                                                        projectUuid={projectUuid}
                                                                        questionnaireUuid={questionnaireUuid}
                                                                        questionUuid={currentQuestion.uuid}
                                                                    />
                                                                ) : uploadingMedia ? (
                                                                    /* Show regular loading state */
                                                                    <div className="space-y-4">
                                                                        <div className="h-16 w-16 mx-auto animate-spin rounded-full border-4 border-gray-300 border-t-iavisus-100"></div>
                                                                        <Text className="text-gray-500">
                                                                            {t('Questionnaires.Builder.UploadingMedia')}
                                                                        </Text>
                                                                    </div>
                                                                ) : (
                                                                    /* Show upload prompt */
                                                                    <>
                                                                        {currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY ? (
                                                                            <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                                                        ) : (
                                                                            <PlayIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                                                        )}
                                                                        
                                                                        <Text className="text-gray-500 mb-2 text-lg">
                                                                            {isDragOver 
                                                                                ? `Drop your ${(currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY || currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION) ? 'image' : 'video'} here`
                                                                                : `Drag & drop or click to upload ${(currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY || currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION) ? 'an image' : 'a video'}`
                                                                            }
                                                                        </Text>
                                                                        
                                                                        <Text className="text-gray-400 text-sm mb-6">
                                                                            {currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY 
                                                                                ? 'Supports: JPG, PNG, GIF, WebP, SVG'
                                                                                : 'Supports: MP4, MOV, AVI, WebM, MKV'
                                                                            }
                                                                            <br />
                                                                            Large files (&gt;50MB) use multi-part uploading
                                                                        </Text>
                                                                        
                                                                        <Text className="text-gray-400 text-xs mb-4">
                                                                            This page will only show the {currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY ? 'image' : 'video'} - no user input required
                                                                        </Text>
                                                                        
                                                                        <Button
                                                                            size="md"
                                                                            icon={currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY ? PhotoIcon : PlayIcon}
                                                                            onClick={() => fileInputRef.current?.click()}
                                                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                            disabled={uploadingMedia}
                                                                        >
                                                                            {(currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY || currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION) ? t('Questionnaires.Builder.BtnChooseImage') : t('Questionnaires.Builder.BtnChooseVideo')}
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                
                                                                {errors.media && (
                                                                    <Text className="text-red-600 text-sm mt-2">
                                                                        {errors.media}
                                                                    </Text>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Hidden file input */}
                                                            <input
                                                                ref={fileInputRef}
                                                                type="file"
                                                                accept={
                                                                    currentQuestion.type === QUESTION_TYPES.IMAGE_ONLY || 
 
                                                                    currentQuestion.type === QUESTION_TYPES.IMAGE_THEN_QUESTION 
                                                                        ? "image/*" 
                                                                        : "video/*"
                                                                }
                                                                onChange={handleFileInputChange}
                                                                className="hidden"
                                                            />
                                                        </div>
                                                    ) : (
                                                        /* Regular Question Layout */
                                                        <div className="space-y-6 text-left">
                                                            {/* Editable Question Text */}
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    value={currentQuestion.question_text}
                                                                    onChange={(e) => updateQuestion(currentQuestion.id, { question_text: e.target.value })}
                                                                    placeholder={t('Questionnaires.Builder.PlaceholderQuestion')}
                                                                    className="w-full text-2xl md:text-3xl font-light bg-transparent border-0 focus:outline-none focus:ring-0 questionnaire-input leading-tight text-left p-0 m-0"
                                                                    style={getTextColorStyle()}
                                                                    maxLength={200}
                                                                />
                                                            </div>
                                                            
                                                            {/* Optional Description Input */}
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    value={currentQuestion.description || ''}
                                                                    onChange={(e) => updateQuestion(currentQuestion.id, { description: e.target.value })}
                                                                    placeholder={t('Questionnaires.Builder.PlaceholderDescriptionOptional')}
                                                                    className="w-full text-base italic bg-transparent border-0 focus:outline-none focus:ring-0 questionnaire-input text-left p-0 m-0"
                                                                    style={getTextColorStyle()}
                                                                    maxLength={500}
                                                                />
                                                            </div>
                                                            
                                                            {/* Answer Preview Area */}
                                                            <div>
                                                                {currentQuestion.type === QUESTION_TYPES.YES_NO ? (
                                                                    /* Yes/No Radio Options */
                                                                    <div className="space-y-3">
                                                                        <div className="flex items-center space-x-3">
                                                                            <input
                                                                                type="radio"
                                                                                id="preview-yes"
                                                                                name="preview-answer"
                                                                                value="yes"
                                                                                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                                                                                style={{ 
                                                                                    accentColor: formData.theme_button_color,
                                                                                    outline: 'none',
                                                                                    border: 'none'
                                                                                }}
                                                                                disabled
                                                                            />
                                                                            <label htmlFor="preview-yes" className="text-lg" style={getTextColorStyle()}>
                                                                                Yes
                                                                            </label>
                                                                        </div>
                                                                        <div className="flex items-center space-x-3">
                                                                            <input
                                                                                type="radio"
                                                                                id="preview-no"
                                                                                name="preview-answer"
                                                                                value="no"
                                                                                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                                                                                style={{ 
                                                                                    accentColor: formData.theme_button_color,
                                                                                    outline: 'none',
                                                                                    border: 'none'
                                                                                }}
                                                                                disabled
                                                                            />
                                                                            <label htmlFor="preview-no" className="text-lg" style={getTextColorStyle()}>
                                                                                No
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                ) : currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICE ? (
                                                                    /* Multiple Choice Options Preview - Dynamic Layout */
                                                                    <div className={getMultipleChoiceLayoutClasses(currentQuestion.display_settings)}>
                                                                        {currentQuestion.options && currentQuestion.options.length > 0 ? (
                                                                            currentQuestion.options.map((option, index) => (
                                                                                <div key={index} className={getOptionContainerClasses(currentQuestion.display_settings)}>
                                                                                    {/* Radio button and text - alignment based on orientation */}
                                                                                    <div className="flex items-center space-x-3">
                                                                                        <input
                                                                                            type="radio"
                                                                                            id={`preview-multiple-${index}`}
                                                                                            name="preview-multiple-choice"
                                                                                            value={index}
                                                                                            className="h-4 w-4 border-0 focus:ring-0 outline-0 flex-shrink-0"
                                                                                            style={{ 
                                                                                                accentColor: formData.theme_button_color,
                                                                                                outline: 'none',
                                                                                                border: 'none'
                                                                                            }}
                                                                                            disabled
                                                                                        />
                                                                                        <label htmlFor={`preview-multiple-${index}`} className="text-lg flex-grow" style={getTextColorStyle()}>
                                                                                            {option.text || `Option ${index + 1}`}
                                                                                        </label>
                                                                                    </div>
                                                                                    {/* Show option image below text if available - full width */}
                                                                                    {option.image_url && (
                                                                                        <div className="w-full">
                                                                                            <img 
                                                                                                src={option.image_url} 
                                                                                                alt={`Option ${index + 1} image`}
                                                                                                className={getImageClasses(currentQuestion.display_settings)}
                                                                                                style={{ borderColor: formData.theme_button_color }}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <div className={getMultipleChoiceLayoutClasses(currentQuestion.display_settings)}>
                                                                                {[1, 2, 3].map((index) => (
                                                                                    <div key={index} className={getOptionContainerClasses(currentQuestion.display_settings)}>
                                                                                        <div className="flex items-center space-x-3">
                                                                                            <input
                                                                                                type="radio"
                                                                                                id={`preview-default-option-${index}`}
                                                                                                name="preview-default-multiple"
                                                                                                value={index}
                                                                                                className="h-4 w-4 border-0 focus:ring-0 outline-0 flex-shrink-0"
                                                                                                style={{ 
                                                                                                    accentColor: formData.theme_button_color,
                                                                                                    outline: 'none',
                                                                                                    border: 'none'
                                                                                                }}
                                                                                                disabled
                                                                                            />
                                                                                            <label htmlFor={`preview-default-option-${index}`} className="text-lg text-gray-400 flex-grow" style={getTextColorStyle(true)}>
                                                                                                Option {index}
                                                                                            </label>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : currentQuestion.type === QUESTION_TYPES.RATING ? (
                                                                    /* Rating Options Preview */
                                                                    <div className="space-y-3">
                                                                        {currentQuestion.options && currentQuestion.options.length > 0 ? (
                                                                            currentQuestion.options.map((option, index) => (
                                                                                <div key={index} className="flex items-center space-x-3">
                                                                                    <input
                                                                                        type="radio"
                                                                                        id={`preview-rating-${index}`}
                                                                                        name="preview-rating"
                                                                                        value={index}
                                                                                        className="h-4 w-4 border-0 focus:ring-0 outline-0"
                                                                                        style={{ 
                                                                                            accentColor: formData.theme_button_color,
                                                                                            outline: 'none',
                                                                                            border: 'none'
                                                                                        }}
                                                                                        disabled
                                                                                    />
                                                                                    <label htmlFor={`preview-rating-${index}`} className="text-lg" style={getTextColorStyle()}>
                                                                                        {option.text}
                                                                                    </label>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <div className="space-y-3">
                                                                                {[1, 2, 3, 4, 5].map((rating) => (
                                                                                    <div key={rating} className="flex items-center space-x-3">
                                                                                        <input
                                                                                            type="radio"
                                                                                            id={`preview-default-${rating}`}
                                                                                            name="preview-default-rating"
                                                                                            value={rating}
                                                                                            className="h-4 w-4 border-0 focus:ring-0 outline-0"
                                                                                            style={{ 
                                                                                                accentColor: formData.theme_button_color,
                                                                                                outline: 'none',
                                                                                                border: 'none'
                                                                                            }}
                                                                                            disabled
                                                                                        />
                                                                                        <label htmlFor={`preview-default-${rating}`} className="text-lg" style={getTextColorStyle()}>
                                                                                            {rating === 1 ? t('Questionnaires.Builder.RatingOptionExcellent') : rating === 2 ? t('Questionnaires.Builder.RatingOptionGood') : rating === 3 ? t('Questionnaires.Builder.RatingOptionAverage') : rating === 4 ? t('Questionnaires.Builder.RatingOptionPoor') : t('Questionnaires.Builder.RatingOptionVeryPoor')}
                                                                                        </label>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : currentQuestion.type === QUESTION_TYPES.TEXT_ONLY ? (
                                                                    /* Text Only - No Input Required */
                                                                    <div className="text-center text-gray-500 italic py-4">
                                                                        {t('Questionnaires.Builder.TextPageNoInput')}
                                                                    </div>
                                                                ) : (
                                                                    /* Text Input for Other Question Types */
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            placeholder={t('Questionnaires.Builder.PlaceholderAnswerHere')}
                                                                            className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input"
                                                                            style={{ 
                                                                                color: formData.theme_text_color,
                                                                                borderBottomColor: formData.theme_button_color
                                                                            }}
                                                                            readOnly
                                                                        />
                                                                        
                                                                        {/* Helper Text for Text Questions */}
                                                                        <p className="text-sm mt-2 text-left p-0 m-0" style={getTextColorStyle(true)}>
                                                                            {t('Questionnaires.Builder.HelperLineBreak')}
                                                                        </p>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>


                                                {/* Multiple Choice Options */}
                                                {(currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICE || currentQuestion.type === QUESTION_TYPES.RATING) && (
                                                    <div>
                                                        <div className="flex items-center justify-between mb-4">
                                                            <label className="block text-sm font-medium text-gray-700">
                                                                {currentQuestion.type === QUESTION_TYPES.RATING ? t('Questionnaires.Builder.LabelRatingOptions') : t('Questionnaires.Builder.LabelOptions')}
                                                            </label>
                                                            <Button
                                                                size="xs"
                                                                icon={PlusIcon}
                                                                onClick={() => addOption(currentQuestion.id)}
                                                                className="bg-iavisus-100 text-white"
                                                            >
                                                                {t('Questionnaires.Builder.BtnAddOption')}
                                                            </Button>
                                                        </div>

                                                        {(!currentQuestion.options || currentQuestion.options.length === 0) ? (
                                                            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                                                <Text className="text-gray-500 mb-4">
                                                                    {t('Questionnaires.Builder.NoOptionsYet')}
                                                                </Text>
                                                                <Button
                                                                    size="sm"
                                                                    icon={PlusIcon}
                                                                    onClick={() => addOption(currentQuestion.id)}
                                                                    className="bg-iavisus-100 text-white"
                                                                >
                                                                    {t('Questionnaires.Builder.BtnAddFirstOption')}
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-3">
                                                                {(currentQuestion.options || []).map((option, optIndex) => (
                                                                    <div key={option.id || optIndex} className={`p-3 bg-gray-50 rounded-lg ${currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICE ? 'space-y-3' : 'flex items-center space-x-3'}`}>
                                                                        {/* Option Number and Text */}
                                                                        <div className={`flex items-center space-x-3 ${currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICE ? 'mb-0' : ''}`}>
                                                                            <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs">
                                                                                {optIndex + 1}
                                                                            </div>
                                                                            <TextInput
                                                                                placeholder={currentQuestion.type === QUESTION_TYPES.RATING ? t('Questionnaires.Builder.PlaceholderRatingLabel') : t('Questionnaires.Builder.PlaceholderOptionText')}
                                                                                value={option.text}
                                                                                onChange={(e) => updateOption(currentQuestion.id, optIndex, { text: e.target.value })}
                                                                                className="flex-1"
                                                                            />
                                                                            <button
                                                                                onClick={() => deleteOption(currentQuestion.id, optIndex)}
                                                                                className="text-red-500 hover:text-red-700"
                                                                            >
                                                                                <TrashIcon className="h-4 w-4" />
                                                                            </button>
                                                                        </div>

                                                                        {/* Image Upload Area for Image Choice and Multiple Choice Options */}
                                                                        {currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICE && (
                                                                            <div className="ml-9">
                                                                                {option.image_url ? (
                                                                                    /* Show uploaded image */
                                                                                    <div className="flex items-center space-x-3">
                                                                                        <img 
                                                                                            src={option.image_url}
                                                                                            alt={`Option ${optIndex + 1} image`}
                                                                                            className="w-16 h-16 object-cover rounded-lg border-2 border-gray-200"
                                                                                            onError={(e) => {
                                                                                                console.error(`Failed to load option image:`, option.image_url)
                                                                                                e.target.style.display = 'none'
                                                                                            }}
                                                                                        />
                                                                                        <div className="flex flex-col space-y-2">
                                                                                            <Button
                                                                                                size="xs"
                                                                                                icon={PhotoIcon}
                                                                                                onClick={() => {
                                                                                                    const input = document.createElement('input')
                                                                                                    input.type = 'file'
                                                                                                    input.accept = 'image/*'
                                                                                                    input.onchange = (e) => handleOptionImageUpload(currentQuestion.id, optIndex, e.target.files[0])
                                                                                                    input.click()
                                                                                                }}
                                                                                                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                                                            >
                                                                                                {t('Questionnaires.Builder.BtnReplaceImage')}
                                                                                            </Button>
                                                                                            <Button
                                                                                                size="xs"
                                                                                                icon={TrashIcon}
                                                                                                onClick={() => updateOption(currentQuestion.id, optIndex, { image_url: null })}
                                                                                                className="bg-red-100 text-red-700 hover:bg-red-200"
                                                                                            >
                                                                                                {t('Questionnaires.Builder.BtnRemoveImage')}
                                                                                            </Button>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    /* Show upload button */
                                                                                    <Button
                                                                                        size="xs"
                                                                                        icon={PhotoIcon}
                                                                                        onClick={() => {
                                                                                            const input = document.createElement('input')
                                                                                            input.type = 'file'
                                                                                            input.accept = 'image/*'
                                                                                            input.onchange = (e) => handleOptionImageUpload(currentQuestion.id, optIndex, e.target.files[0])
                                                                                            input.click()
                                                                                        }}
                                                                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                                    >
                                                                                        {t('Questionnaires.Builder.AddImage')}
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Right Panel - Settings and Options */}
                            <div className="w-80 border-l bg-gray-50 p-6 overflow-y-auto">
                                {currentStep === 0 ? (
                                    /* Basic Settings */
                                    <div className="space-y-6">
                                        <div>
                                            <Text className="font-semibold text-gray-900 mb-4">{t('Questionnaires.Builder.HeaderSettings')}</Text>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <label htmlFor="anonymous" className="text-sm text-gray-700">
                                                    {t('Questionnaires.Builder.LabelAnonymousResponses')}
                                                </label>
                                                <input
                                                    type="checkbox"
                                                    id="anonymous"
                                                    checked={formData.anonymous_responses}
                                                    onChange={(e) => updateFormData({ anonymous_responses: e.target.checked })}
                                                    className="h-4 w-4 border-0 focus:ring-0 outline-0 rounded"
                                                    style={{
                                                        accentColor: formData.theme_button_color,
                                                        outline: 'none',
                                                        border: 'none'
                                                    }}
                                                />
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <label htmlFor="one_response" className="text-sm text-gray-700">
                                                    {t('Questionnaires.Builder.LabelOneResponsePerUser')}
                                                </label>
                                                <input
                                                    type="checkbox"
                                                    id="one_response"
                                                    checked={formData.one_response_per_user}
                                                    onChange={(e) => updateFormData({ one_response_per_user: e.target.checked })}
                                                    className="h-4 w-4 border-0 focus:ring-0 outline-0 rounded"
                                                    style={{
                                                        accentColor: formData.theme_button_color,
                                                        outline: 'none',
                                                        border: 'none'
                                                    }}
                                                />
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <label htmlFor="is_timed" className="text-sm text-gray-700">
                                                    {t('Questionnaires.Builder.LabelTimed')}
                                                </label>
                                                <input
                                                    type="checkbox"
                                                    id="is_timed"
                                                    checked={formData.is_timed}
                                                    onChange={(e) => updateFormData({ is_timed: e.target.checked })}
                                                    className="h-4 w-4 border-0 focus:ring-0 outline-0 rounded"
                                                    style={{
                                                        accentColor: formData.theme_button_color,
                                                        outline: 'none',
                                                        border: 'none'
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <Text className="font-semibold text-gray-900">{t('Questionnaires.Builder.LabelTheme')}</Text>

                                            <div>
                                                <label className="block text-xs text-gray-600 mb-2">{t('Questionnaires.Builder.LabelTextColor')}</label>
                                                <input
                                                    type="color"
                                                    value={formData.theme_text_color}
                                                    onChange={(e) => updateFormData({ theme_text_color: e.target.value })}
                                                    className="h-10 w-full rounded-md border-0 outline-0"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs text-gray-600 mb-2">{t('Questionnaires.Builder.LabelButtonColor')}</label>
                                                <input
                                                    type="color"
                                                    value={formData.theme_button_color}
                                                    onChange={(e) => updateFormData({ theme_button_color: e.target.value })}
                                                    className="h-10 w-full rounded-md border-0 outline-0"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs text-gray-600 mb-2">{t('Questionnaires.Builder.LabelBackgroundColor')}</label>
                                                <input
                                                    type="color"
                                                    value={formData.theme_background_color}
                                                    onChange={(e) => updateFormData({ theme_background_color: e.target.value })}
                                                    className="h-10 w-full rounded-md border-0 outline-0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Question Settings */
                                    <div className="space-y-6">
                                        {currentQuestion && (
                                            <>
                                                {(currentQuestion.type === QUESTION_TYPES.MULTI_SECTION || true) && currentSectionIndex === -1 ? (
                                                    /* Multi-Section Page Settings */
                                                    <>
                                                        <div>
                                                            <Text className="font-semibold text-gray-900 mb-4">{t('Questionnaires.Builder.HeaderPageSections')}</Text>
                                                        </div>

                                                        <div className="space-y-4">
                                                            {/* Draggable Sections List */}
                                                            <div className="space-y-2">
                                                                {currentQuestion.sections && currentQuestion.sections.length > 0 ? (
                                                                    <DndContext 
                                                                        sensors={sensors}
                                                                        collisionDetection={closestCenter}
                                                                        onDragEnd={handleSectionDragEnd}
                                                                    >
                                                                        <SortableContext 
                                                                            items={currentQuestion.sections.map(s => s.id)}
                                                                            strategy={verticalListSortingStrategy}
                                                                        >
                                                                            {currentQuestion.sections.map((section, index) => (
                                                                                <SortableSection
                                                                                    key={section.id}
                                                                                    section={section}
                                                                                    index={index}
                                                                                    onEdit={(sectionIndex) => setCurrentSectionIndex(sectionIndex)}
                                                                                    onRemove={(sectionId) => removeSection(currentQuestion.id, sectionId)}
                                                                                />
                                                                            ))}
                                                                        </SortableContext>
                                                                    </DndContext>
                                                                ) : (
                                                                    <div className="text-center py-6 text-gray-500 text-sm">
                                                                        {t('Questionnaires.Builder.SectionNoSectionsAdd')}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Add Section Dropdown */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    {t('Questionnaires.Builder.LabelAddNewSection')}
                                                                </label>
                                                                <div className="flex space-x-2">
                                                                    <select
                                                                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100 text-sm"
                                                                        onChange={(e) => {
                                                                            if (e.target.value) {
                                                                                addSection(currentQuestion.id, e.target.value);
                                                                                e.target.value = '';
                                                                            }
                                                                        }}
                                                                        defaultValue=""
                                                                    >
                                                                        <option value="">{t('Questionnaires.Builder.LabelChooseSectionType')}</option>
                                                                        <option value={QUESTION_TYPES.TEXT}>{t('Questionnaires.Builder.TypeTextInput')}</option>
                                                                        <option value={QUESTION_TYPES.TEXT_ONLY}>{t('Questionnaires.Builder.TypeTextDisplay')}</option>
                                                                        <option value={QUESTION_TYPES.MULTIPLE_CHOICE}>{t('Questionnaires.Builder.TypeMultipleChoice')}</option>
                                                                        <option value={QUESTION_TYPES.YES_NO}>{t('Questionnaires.Builder.TypeYesNo')}</option>
                                                                        <option value={QUESTION_TYPES.RATING}>{t('Questionnaires.Builder.TypeRating')}</option>
                                                                        <option value={QUESTION_TYPES.TABLE}>{t('Questionnaires.Builder.TypeTableMatrix')}</option>
                                                                        <option value={QUESTION_TYPES.IMAGE_ONLY}>{t('Questionnaires.Builder.TypeImageOnly')}</option>
                                                                        <option value={QUESTION_TYPES.VIDEO_ONLY}>{t('Questionnaires.Builder.TypeVideoOnly')}</option>
                                                                        <option value={QUESTION_TYPES.AUDIO_ONLY}>{t('Questionnaires.Builder.TypeAudioOnly')}</option>
                                                                        <option value={QUESTION_TYPES.VOICE_RECORDING}>{t('Questionnaires.Builder.TypeVoiceRecording')}</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            {/* Page-level Timing Settings */}
                                                            <div className="border-t border-gray-200 pt-6">
                                                                <Text className="font-semibold text-gray-900 mb-4">{t('Questionnaires.Builder.HeaderPageTiming')}</Text>
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                            {t('Questionnaires.Builder.LabelViewDuration')}
                                                                        </label>
                                                                        <TextInput
                                                                            type="number"
                                                                            min="1"
                                                                            max="300"
                                                                            value={currentQuestion.view_duration || ''}
                                                                            onChange={(e) => {
                                                                                setFormData(prev => ({
                                                                                    ...prev,
                                                                                    questions: prev.questions.map(q => 
                                                                                        q.id === currentQuestion.id 
                                                                                            ? { ...q, view_duration: e.target.value === '' ? null : e.target.value }
                                                                                            : q
                                                                                    )
                                                                                }));
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                                                                setFormData(prev => ({
                                                                                    ...prev,
                                                                                    questions: prev.questions.map(q => 
                                                                                        q.id === currentQuestion.id 
                                                                                            ? { ...q, view_duration: value }
                                                                                            : q
                                                                                    )
                                                                                }));
                                                                                updateQuestion(currentQuestion.id, { view_duration: value });
                                                                            }}
                                                                            placeholder={t('Questionnaires.Builder.PlaceholderDurationSeconds')}
                                                                            className="w-full"
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                            {t('Questionnaires.Builder.LabelAnswerDuration')}
                                                                        </label>
                                                                        <TextInput
                                                                            type="number"
                                                                            min="5"
                                                                            max="600"
                                                                            value={currentQuestion.answer_duration || ''}
                                                                            onChange={(e) => {
                                                                                setFormData(prev => ({
                                                                                    ...prev,
                                                                                    questions: prev.questions.map(q => 
                                                                                        q.id === currentQuestion.id 
                                                                                            ? { ...q, answer_duration: e.target.value === '' ? null : e.target.value }
                                                                                            : q
                                                                                    )
                                                                                }));
                                                                            }}
                                                                            onBlur={async (e) => {
                                                                                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                                                                if (currentQuestion.uuid && projectUuid && questionnaireUuid) {
                                                                                    try {
                                                                                        const response = await updateQuestionAPI(projectUuid, questionnaireUuid, currentQuestion.uuid, { answer_duration: value });
                                                                                        setFormData(prev => ({
                                                                                            ...prev,
                                                                                            questions: prev.questions.map(q => 
                                                                                                q.id === currentQuestion.id 
                                                                                                    ? { ...q, answer_duration: value }
                                                                                                    : q
                                                                                            )
                                                                                        }));
                                                                                        if (onQuestionUpdate) {
                                                                                            onQuestionUpdate(response.question);
                                                                                        }
                                                                                    } catch (error) {
                                                                                        console.error('Save failed:', error);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            placeholder={t('Questionnaires.Builder.PlaceholderDurationSeconds')}
                                                                            className="w-full"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <p className="text-xs text-gray-500 mt-2">
                                                                    {t('Questionnaires.Builder.PageTimingNote')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (currentQuestion.type === QUESTION_TYPES.MULTI_SECTION || true) && currentSectionIndex >= 0 ? (
                                                    /* Current Section Settings */
                                                    <>
                                                        <div className="flex items-center justify-between mb-4">
                                                            <Text className="font-semibold text-gray-900">
                                                                {t('Questionnaires.Builder.HeaderSectionN', { number: currentSectionIndex + 1 })}
                                                            </Text>
                                                            <button
                                                                onClick={() => setCurrentSectionIndex(-1)}
                                                                className="text-sm text-iavisus-100 hover:text-iavisus-200"
                                                            >
                                                                {t('Questionnaires.Builder.LinkBackToSections')}
                                                            </button>
                                                        </div>

                                                        {getCurrentSection() && (
                                                            <div className="space-y-4">
                                                                {/* Section Type */}
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                        {t('Questionnaires.Builder.LabelSectionType')}
                                                                    </label>
                                                                    <select
                                                                        value={getCurrentSection().type}
                                                                        onChange={(e) => handleSectionTypeChange(currentQuestion.id, getCurrentSection().id, e.target.value)}
                                                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                                    >
                                                                        <option value={QUESTION_TYPES.TEXT_ONLY}>{t('Questionnaires.Builder.TypeTextOnly')}</option>
                                                                        <option value={QUESTION_TYPES.TEXT}>{t('Questionnaires.Builder.TypeTextResponse')}</option>
                                                                        <option value={QUESTION_TYPES.MULTIPLE_CHOICE}>{t('Questionnaires.Builder.TypeMultipleChoice')}</option>
                                                                        <option value={QUESTION_TYPES.YES_NO}>{t('Questionnaires.Builder.TypeYesNo')}</option>
                                                                        <option value={QUESTION_TYPES.RATING}>{t('Questionnaires.Builder.TypeRating')}</option>
                                                                        <option value={QUESTION_TYPES.TABLE}>{t('Questionnaires.Builder.TypeTableMatrix')}</option>
                                                                        <option value={QUESTION_TYPES.IMAGE_ONLY}>{t('Questionnaires.Builder.TypeImageOnly')}</option>
                                                                        <option value={QUESTION_TYPES.VIDEO_ONLY}>{t('Questionnaires.Builder.TypeVideoOnly')}</option>
                                                                        <option value={QUESTION_TYPES.AUDIO_ONLY}>{t('Questionnaires.Builder.TypeAudioOnly')}</option>
                                                                        <option value={QUESTION_TYPES.VOICE_RECORDING}>{t('Questionnaires.Builder.TypeVoiceRecording')}</option>
                                                                    </select>
                                                                </div>

                                                                {/* Section-specific settings would go here */}
                                                                {/* For now, we'll add basic settings that apply to most sections */}
                                                                
                                                                {/* Display Settings for sections that support them */}
                                                                {getCurrentSection().type === QUESTION_TYPES.MULTIPLE_CHOICE && (
                                                                    <div className="space-y-4">
                                                                        <Text className="font-medium text-gray-900">{t('Questionnaires.Builder.HeaderDisplaySettings')}</Text>

                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                {t('Questionnaires.Builder.LabelLayoutType')}
                                                                            </label>
                                                                            <select
                                                                                value={getCurrentSection().display_settings?.layout_type || 'grid'}
                                                                                onChange={(e) => {
                                                                                    const newDisplaySettings = {
                                                                                        ...getCurrentSection().display_settings,
                                                                                        layout_type: e.target.value
                                                                                    };
                                                                                    updateSection(currentQuestion.id, getCurrentSection().id, { display_settings: newDisplaySettings });
                                                                                }}
                                                                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                                            >
                                                                                <option value="grid">{t('Questionnaires.Builder.LayoutGrid')}</option>
                                                                                <option value="horizontal">{t('Questionnaires.Builder.LayoutHorizontal')}</option>
                                                                                <option value="vertical">{t('Questionnaires.Builder.LayoutVertical')}</option>
                                                                            </select>
                                                                        </div>

                                                                        {getCurrentSection().display_settings?.layout_type === 'grid' && (
                                                                            <div>
                                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                    {t('Questionnaires.Builder.LabelItemsPerRow')}
                                                                                </label>
                                                                                <select
                                                                                    value={getCurrentSection().display_settings?.items_per_row || 3}
                                                                                    onChange={(e) => {
                                                                                        const newDisplaySettings = {
                                                                                            ...getCurrentSection().display_settings,
                                                                                            items_per_row: parseInt(e.target.value)
                                                                                        };
                                                                                        updateSection(currentQuestion.id, getCurrentSection().id, { display_settings: newDisplaySettings });
                                                                                    }}
                                                                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                                                >
                                                                                    <option value={1}>1 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={2}>2 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={3}>3 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={4}>4 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={5}>5 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={6}>6 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={7}>7 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={8}>8 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={9}>9 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                    <option value={10}>10 {t('Questionnaires.Builder.PerRow')}</option>
                                                                                </select>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Table/Matrix Settings */}
                                                                {getCurrentSection().type === QUESTION_TYPES.TABLE && (
                                                                    <div className="space-y-4">
                                                                        <Text className="font-medium text-gray-900">{t('Questionnaires.Builder.HeaderTableConfiguration')}</Text>
                                                                        
                                                                        {/* Create Table Button if no table data exists */}
                                                                        {!getCurrentSection().table_data && (
                                                                            <div>
                                                                                <Button
                                                                                    size="sm"
                                                                                    icon={PlusIcon}
                                                                                    onClick={() => {
                                                                                        const tableData = initializeDefaultTableOptions()
                                                                                        updateSection(currentQuestion.id, getCurrentSection().id, { table_data: tableData })
                                                                                    }}
                                                                                    className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                                >
                                                                                    {t('Questionnaires.Builder.BtnInitializeTable')}
                                                                                </Button>
                                                                            </div>
                                                                        )}

                                                                        {/* Table Management Controls */}
                                                                        {getCurrentSection().table_data && (
                                                                            <div className="space-y-3">
                                                                                <div className="flex space-x-2">
                                                                                    <Button
                                                                                        size="xs"
                                                                                        icon={PlusIcon}
                                                                                        onClick={() => addTableRow(currentQuestion.id, getCurrentSection().id)}
                                                                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                                    >
                                                                                        {t('Questionnaires.Builder.BtnAddRow')}
                                                                                    </Button>
                                                                                    <Button
                                                                                        size="xs"
                                                                                        icon={PlusIcon}
                                                                                        onClick={() => addTableColumn(currentQuestion.id, getCurrentSection().id)}
                                                                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                                                                    >
                                                                                        {t('Questionnaires.Builder.BtnAddColumn')}
                                                                                    </Button>
                                                                                </div>

                                                                                <div className="text-sm text-gray-600">
                                                                                    <p>Rows: {getCurrentSection().table_data.rows?.length || 0}</p>
                                                                                    <p>Columns: {getCurrentSection().table_data.columns?.length || 0}</p>
                                                                                </div>

                                                                                <div className="space-y-2">
                                                                                    <label className="block text-sm font-medium text-gray-700">
                                                                                        {t('Questionnaires.Builder.LabelHeaderMode')}
                                                                                    </label>
                                                                                    <select
                                                                                        value={getCurrentSection().table_data.header_mode || 'headers'}
                                                                                        onChange={(e) => {
                                                                                            const newTableData = {
                                                                                                ...getCurrentSection().table_data,
                                                                                                header_mode: e.target.value
                                                                                            };
                                                                                            updateSection(currentQuestion.id, getCurrentSection().id, { table_data: newTableData });
                                                                                        }}
                                                                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                                                    >
                                                                                        <option value="headers">{t('Questionnaires.Builder.LabelHeaderModeHeaders')}</option>
                                                                                        <option value="cells">{t('Questionnaires.Builder.LabelHeaderModeCells')}</option>
                                                                                    </select>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Timing Controls for sections that need user input */}
                                                                {getCurrentSection().type !== QUESTION_TYPES.IMAGE_ONLY && 
                                                                 getCurrentSection().type !== QUESTION_TYPES.VIDEO_ONLY && 
                                                                 getCurrentSection().type !== QUESTION_TYPES.TEXT_ONLY && (
                                                                    <div className="grid grid-cols-1 gap-4">
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                {t('Questionnaires.Builder.LabelViewDurationSec')}
                                                                            </label>
                                                                            <TextInput
                                                                                type="number"
                                                                                min="1"
                                                                                max="300"
                                                                                value={getCurrentSection().view_duration || ''}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value === '' ? null : e.target.value
                                                                                    updateSection(currentQuestion.id, getCurrentSection().id, { view_duration: value })
                                                                                }}
                                                                                placeholder={t('Questionnaires.Builder.LabelViewDurationSecShort')}
                                                                                className="w-full"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                {t('Questionnaires.Builder.LabelAnswerDurationSec')}
                                                                            </label>
                                                                            <TextInput
                                                                                type="number"
                                                                                min="1"
                                                                                max="600"
                                                                                value={getCurrentSection().answer_duration || ''}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value === '' ? null : e.target.value
                                                                                    updateSection(currentQuestion.id, getCurrentSection().id, { answer_duration: value })
                                                                                }}
                                                                                placeholder={t('Questionnaires.Builder.LabelAnswerDurationSecShort')}
                                                                                className="w-full"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Required Field Toggle for sections that need user input */}
                                                                {getCurrentSection().type !== QUESTION_TYPES.IMAGE_ONLY && 
                                                                 getCurrentSection().type !== QUESTION_TYPES.VIDEO_ONLY && 
                                                                 getCurrentSection().type !== QUESTION_TYPES.TEXT_ONLY && (
                                                                    <div className="flex items-center justify-between">
                                                                        <label className="block text-sm font-medium text-gray-700">
                                                                            {t('Questionnaires.Builder.LabelRequired')}
                                                                        </label>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={getCurrentSection().required || false}
                                                                            onChange={(e) => updateSection(currentQuestion.id, getCurrentSection().id, { required: e.target.checked })}
                                                                            className="h-4 w-4 text-iavisus-100 focus:ring-iavisus-100 border-gray-300 rounded"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    /* Regular Question Settings */
                                                    <>
                                                        <div>
                                                            <Text className="font-semibold text-gray-900 mb-4">{t('Questionnaires.Builder.HeaderQuestionSettings')}</Text>
                                                        </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('Questionnaires.Builder.LabelPageType')}
                                                    </label>
                                                    <div className="w-full p-3 bg-gray-100 rounded-md border border-gray-300 text-gray-700">
                                                        {t('Questionnaires.Builder.TypeMultiSectionPageDisplay')}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {t('Questionnaires.Builder.MultiSectionPageDescription')}
                                                    </p>
                                                </div>

                                                {/* Timing fields always shown for multi-section pages */}
                                                {true && (
                                                    <div className="grid grid-cols-1 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Questionnaires.Builder.LabelViewDurationSec')}
                                                            </label>
                                                            <TextInput
                                                                type="number"
                                                                min="1"
                                                                max="300"
                                                                value={currentQuestion.view_duration || ''}
                                                                onChange={(e) => {
                                                                    // Just update display, don't save on every keystroke
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        questions: prev.questions.map(q =>
                                                                            q.id === currentQuestion.id
                                                                                ? { ...q, view_duration: e.target.value === '' ? null : e.target.value }
                                                                                : q
                                                                        )
                                                                    }));
                                                                }}
                                                                onBlur={(e) => {
                                                                    // Save when user finishes editing (clicks away or tabs)
                                                                    const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                                                    
                                                                    // Update local state with the parsed value
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        questions: prev.questions.map(q => 
                                                                            q.id === currentQuestion.id 
                                                                                ? { ...q, view_duration: value }
                                                                                : q
                                                                        )
                                                                    }));
                                                                    
                                                                    updateQuestion(currentQuestion.id, { view_duration: value });
                                                                }}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Questionnaires.Builder.LabelAnswerDurationSec')}
                                                            </label>
                                                            <TextInput
                                                                type="number"
                                                                min="5"
                                                                max="600"
                                                                value={currentQuestion.answer_duration || ''}
                                                                onChange={(e) => {
                                                                    // Just update display, don't save on every keystroke
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        questions: prev.questions.map(q => 
                                                                            q.id === currentQuestion.id 
                                                                                ? { ...q, answer_duration: e.target.value === '' ? null : e.target.value }
                                                                                : q
                                                                        )
                                                                    }));
                                                                }}
                                                                onBlur={async (e) => {
                                                                    // Save when user finishes editing (clicks away or tabs)
                                                                    const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                                                    
                                                                    if (currentQuestion.uuid && projectUuid && questionnaireUuid) {
                                                                        try {
                                                                            const response = await updateQuestionAPI(projectUuid, questionnaireUuid, currentQuestion.uuid, { answer_duration: value });
                                                                            
                                                                            // Update local state with server response
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                questions: prev.questions.map(q => 
                                                                                    q.id === currentQuestion.id 
                                                                                        ? { ...q, answer_duration: response.question.answer_duration }
                                                                                        : q
                                                                                )
                                                                            }));
                                                                            
                                                                            // Update parent list directly
                                                                            if (onQuestionUpdate) {
                                                                                onQuestionUpdate(response.question);
                                                                            }
                                                                        } catch (error) {
                                                                            console.error('Save failed:', error);
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Display Settings for Multiple Choice Questions */}
                                                {currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICE && (
                                                    <div className="border-t border-gray-200 pt-6">
                                                        <Text className="font-semibold text-gray-900 mb-4">{t('Questionnaires.Builder.HeaderDisplaySettings')}</Text>

                                                        <div className="space-y-4">
                                                            {/* Layout Type */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    {t('Questionnaires.Builder.LabelLayoutType')}
                                                                </label>
                                                                <select
                                                                    value={currentQuestion.display_settings?.layout_type || 'grid'}
                                                                    onChange={(e) => {
                                                                        const newDisplaySettings = {
                                                                            ...currentQuestion.display_settings,
                                                                            layout_type: e.target.value
                                                                        };
                                                                        updateQuestion(currentQuestion.id, { display_settings: newDisplaySettings });
                                                                    }}
                                                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                                >
                                                                    <option value="grid">{t('Questionnaires.Builder.LayoutGrid')}</option>
                                                                    <option value="horizontal">{t('Questionnaires.Builder.LayoutHorizontal')}</option>
                                                                    <option value="vertical">{t('Questionnaires.Builder.LayoutVertical')}</option>
                                                                </select>
                                                            </div>
                                                            
                                                            {/* Items per Row - Only for Grid Layout */}
                                                            {(currentQuestion.display_settings?.layout_type || 'grid') === 'grid' && (
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                        {t('Questionnaires.Builder.LabelItemsPerRow')}
                                                                    </label>
                                                                    <select
                                                                        value={currentQuestion.display_settings?.items_per_row || 3}
                                                                        onChange={(e) => {
                                                                            const newDisplaySettings = {
                                                                                ...currentQuestion.display_settings,
                                                                                items_per_row: parseInt(e.target.value)
                                                                            };
                                                                            updateQuestion(currentQuestion.id, { display_settings: newDisplaySettings });
                                                                        }}
                                                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                                    >
                                                                        <option value={1}>1 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={2}>2 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={3}>3 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={4}>4 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={5}>5 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={6}>6 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={7}>7 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={8}>8 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={9}>9 {t('Questionnaires.Builder.PerRow')}</option>
                                                                        <option value={10}>10 {t('Questionnaires.Builder.PerRow')}</option>
                                                                    </select>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Only show required field for question types that need user input */}
                                                {currentQuestion.type !== QUESTION_TYPES.IMAGE_ONLY && currentQuestion.type !== QUESTION_TYPES.VIDEO_ONLY && (
                                                    <div className="flex items-center justify-between">
                                                        <label className="text-sm text-gray-700">
                                                            {t('Questionnaires.Builder.LabelRequiredQuestion')}
                                                        </label>
                                                        <input
                                                            type="checkbox"
                                                            checked={currentQuestion.required}
                                                            onChange={(e) => updateQuestion(currentQuestion.id, { required: e.target.checked })}
                                                            className="h-4 w-4 border-0 focus:ring-0 outline-0 rounded"
                                                            style={{
                                                                accentColor: formData.theme_button_color,
                                                                outline: 'none',
                                                                border: 'none'
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                <div className="border-t border-gray-200 pt-4">
                                                    <button
                                                        onClick={() => handleDeleteQuestion(currentQuestion.id)}
                                                        className="w-full px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors flex items-center justify-center space-x-2"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                        <span>{t('Questionnaires.Builder.BtnDeleteQuestion')}</span>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t bg-white p-6">
                        <div className="flex items-center justify-end">
                            <div className="flex items-center space-x-3">
                                <Button
                                    variant="secondary"
                                    onClick={onClose}
                                >
                                    {t('Questionnaires.Builder.BtnCancel')}
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                    loading={isSubmitting}
                                >
                                    {currentStep === 0 && formData.questions.length === 0
                                        ? t('Questionnaires.Builder.BtnContinue')
                                        : (isEditing ? t('Questionnaires.Builder.BtnUpdateQuestionnaire') : t('Questionnaires.Builder.BtnCreateQuestionnaire'))
                                    }
                                </Button>
                            </div>
                        </div>

                        {errors.submit && (
                            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                                <Text className="text-red-600 text-sm">{errors.submit}</Text>
                            </div>
                        )}
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>

        {/* Delete Question Confirmation Modal */}
        <Dialog open={showDeleteModal} onClose={cancelDeleteQuestion} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white p-6">
                    <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                        {t('Questionnaires.Builder.DeleteQuestionTitle')}
                    </Dialog.Title>

                    <Text className="text-gray-600 mb-6">
                        {t('Questionnaires.Builder.DeleteQuestionConfirm')}
                        {questionToDelete?.question_text && (
                            <span className="block mt-2 font-medium text-gray-800">
                                "{questionToDelete.question_text}"
                            </span>
                        )}
                    </Text>

                    <div className="flex space-x-3">
                        <Button
                            onClick={cancelDeleteQuestion}
                            variant="secondary"
                            className="flex-1"
                            disabled={deletingQuestion}
                        >
                            {t('Questionnaires.Builder.BtnCancel')}
                        </Button>
                        <Button
                            onClick={confirmDeleteQuestion}
                            className="flex-1 bg-red-600 text-white hover:bg-red-700"
                            loading={deletingQuestion}
                            disabled={deletingQuestion}
                        >
                            {t('Questionnaires.Builder.DeleteQuestionTitle')}
                        </Button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
        </>
    )
}

export default ImprovedQuestionnaireBuilderModal